import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";

import { remixAiUpgradePlan } from "../utils/canUseAiRemixText";
import unwrapBundlerHtml from "../utils/unwrapBundlerHtml";
import {
  attachmentFromClipboard,
  htmlOrMarkdownFilesFromDataTransfer,
  isAiImportTextFile,
  MAX_IMPORTED_ATTACHMENTS,
  readClipboardFileText,
} from "../utils/aiPasteAttachment";
import {
  AI_CHAT_MD_TABLE_WRAP_CLASS,
  formatMarkdownTables,
} from "../utils/aiChatMarkdown";
import {
  billableActionsFromResponse,
  dispatchBillableActionsUsed,
  LOW_EFFORT_IMPORT_ACTIONS,
  HIGH_EFFORT_IMPORT_ACTIONS,
} from "../utils/billableActionsMeter";
import {
  feedbackRowHtml,
  submitChatFeedback,
  buildSupportChatPrefill,
} from "./shared/chatFeedback";
import {
  dataTransferHasFiles,
  dataTransferHasImages,
  imageDropLimitMessage,
  isImageFile,
  takeImageFiles,
  UPLOAD_WAIT_MESSAGE,
  uploadFailureMessage,
  uploadLocalImageFile,
} from "../utils/aiChatImageDrop";
import { createChatLiveness } from "../utils/chatLiveness";

const INSIGHTS_PREFILL_PROMPTS = {
  "Top campaigns":
    "Report on my top campaigns for the last 30 days using the website analytics snapshot. List the top campaigns with views, conversions, conversion rate, revenue where available, custom goals where available, and bookings. Summarize the report immediately—do not ask clarifying questions.",
  "Active split-tests":
    "Report on my active A/B split-test experiments using the website analytics snapshot. List each experiment with campaign name, variant split, and views. Summarize immediately—do not ask clarifying questions.",
  "Last month funnel revenue":
    "Report on my funnel revenue for last month using the website analytics snapshot. Include viewers, conversions, orders, revenue, AOV where available, bookings, and custom goals where available. Summarize immediately—do not ask clarifying questions.",
  "Audit my funnel":
    "Audit my funnel using the website analytics snapshot and connected integrations context. First, detail the current insights: (1) funnel performance trends for the last 30 days (views, conversions, leads, revenue/orders where available, custom goals where available, and bookings), (2) top campaigns, (3) Subscribe volume insights when present—report ConvertFlow new leads versus each connected ESP/CRM's new contacts and ConvertFlow alignment % (always label this ConvertFlow alignment, never ConvertFlow share), (4) audience segmentation assets from connected integrations, and (5) the main bottlenecks or opportunities. Then immediately recommend the best playbook(s) to launch next via recommend_playbook—do not ask clarifying questions first. Ground each remix_prompt in the insights you just shared.",
};

const WEBSITE_PREFILL_PROMPTS = {
  "Audit my funnel": INSIGHTS_PREFILL_PROMPTS["Audit my funnel"],
};

const ACCOUNT_AGENT_PREFILL_PROMPTS = {
  "Account performance":
    "Report on account-wide performance for the last 30 days across websites I can access. Include viewers, conversions, conversion rate, subscribes, orders, revenue, custom goals, and bookings. Summarize immediately—do not ask clarifying questions.",
  "Underperforming websites":
    "Identify underperforming websites I can access for the last 30 days. Rank by conversion rate and period-over-period change. Summarize immediately, then offer to recommend playbooks for the weakest site.",
  "Top campaigns":
    "Report on the top campaigns across websites I can access for the last 30 days. List campaign name, website, views, conversions, conversion rate, revenue, custom goals, and bookings. Summarize immediately.",
  "Export report":
    "Export account-wide website performance for the last 30 days. Use the export tools so I get CSV download buttons. If I mention specific websites, filter the account export to those sites. Also attach website-level export buttons when I name a site. Summarize immediately.",
};

const FUNNEL_BREAKDOWN_PREFILL_PROMPTS = {
  "Where is dropoff highest?":
    "Using this campaign's funnel breakdown snapshot, report where dropoff is highest across steps. Include step views, conversions, and conversion rate. Summarize immediately, then recommend one concrete split test via recommend_experiment—do not ask clarifying questions.",
  "Summarize this funnel":
    "Using this campaign's funnel breakdown snapshot, summarize step-by-step progression, biggest dropoffs, and notable answer selections. Summarize immediately, then recommend one concrete split test via recommend_experiment—do not ask clarifying questions.",
  "What should I test next?":
    "Using this campaign's funnel breakdown snapshot, recommend the highest-leverage split test to run next. Call recommend_experiment with copy-only remix instructions so I can Apply Experiment. Do not ask clarifying questions.",
};

const GENERATE_CUSTOM_TEMPLATE_PREFILL_OPTION = "Generate a custom template";
const GENERATE_CUSTOM_TEMPLATE_PILL_OPTION = "Generate custom template";
const GENERATE_TEMPLATE_LABEL = "Generate template";
const MAX_IMAGE_ATTACHMENTS = 3;
const COMPOSER_MAX_HEIGHT_PX = 160;
const GENERATE_TEMPLATE_SPARKLE_ICON =
  '<svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0"><path d="M10.5086 7.86766L8.97152 7.42808C8.27936 7.23279 7.64886 6.86299 7.14034 6.35408C6.63183 5.84516 6.26232 5.21416 6.06718 4.52145L5.62795 2.9832C5.60586 2.92939 5.56829 2.88336 5.52 2.85096C5.47171 2.81857 5.41488 2.80127 5.35675 2.80127C5.29861 2.80127 5.24179 2.81857 5.1935 2.85096C5.14521 2.88336 5.10763 2.92939 5.08554 2.9832L4.64631 4.52145C4.45117 5.21416 4.08166 5.84516 3.57315 6.35408C3.06464 6.86299 2.43413 7.23279 1.74197 7.42808L0.204942 7.86766C0.145925 7.88443 0.0939831 7.92 0.0569971 7.96898C0.0200111 8.01797 0 8.07769 0 8.13908C0 8.20048 0.0200111 8.2602 0.0569971 8.30918C0.0939831 8.35817 0.145925 8.39374 0.204942 8.4105L1.74197 8.85008C2.43413 9.04538 3.06464 9.41517 3.57315 9.92409C4.08166 10.433 4.45117 11.064 4.64631 11.7567L5.08554 13.295C5.10229 13.354 5.13783 13.406 5.18678 13.443C5.23572 13.48 5.2954 13.5001 5.35675 13.5001C5.41809 13.5001 5.47777 13.48 5.52671 13.443C5.57566 13.406 5.6112 13.354 5.62795 13.295L6.06718 11.7567C6.26232 11.064 6.63183 10.433 7.14034 9.92409C7.64886 9.41517 8.27936 9.04538 8.97152 8.85008L10.5086 8.4105C10.5676 8.39374 10.6195 8.35817 10.6565 8.30918C10.6935 8.2602 10.7135 8.20048 10.7135 8.13908C10.7135 8.07769 10.6935 8.01797 10.6565 7.96898C10.6195 7.92 10.5676 7.88443 10.5086 7.86766Z" fill="#003FFF"/><path d="M12.9087 2.75173L12.2256 2.55636C11.918 2.46956 11.6378 2.30521 11.4118 2.07903C11.1858 1.85284 11.0215 1.5724 10.9348 1.26452L10.7396 0.58086C10.7298 0.556941 10.7131 0.536483 10.6916 0.522085C10.6702 0.507687 10.6449 0.5 10.6191 0.5C10.5932 0.5 10.568 0.507687 10.5465 0.522085C10.525 0.536483 10.5083 0.556941 10.4985 0.58086L10.3033 1.26452C10.2166 1.5724 10.0524 1.85284 9.82635 2.07903C9.60034 2.30521 9.32012 2.46956 9.01249 2.55636L8.32937 2.75173C8.30314 2.75918 8.28005 2.77499 8.26361 2.79676C8.24718 2.81853 8.23828 2.84507 8.23828 2.87236C8.23828 2.89965 8.24718 2.92619 8.26361 2.94796C8.28005 2.96973 8.30314 2.98554 8.32937 2.99299L9.01249 3.18836C9.32012 3.27516 9.60034 3.43951 9.82635 3.6657C10.0524 3.89188 10.2166 4.17233 10.3033 4.4802L10.4985 5.16386C10.506 5.19011 10.5218 5.21322 10.5435 5.22967C10.5653 5.24612 10.5918 5.25502 10.6191 5.25502C10.6463 5.25502 10.6728 5.24612 10.6946 5.22967C10.7164 5.21322 10.7321 5.19011 10.7396 5.16386L10.9348 4.4802C11.0215 4.17233 11.1858 3.89188 11.4118 3.6657C11.6378 3.43951 11.918 3.27516 12.2256 3.18836L12.9087 2.99299C12.935 2.98554 12.9581 2.96973 12.9745 2.94796C12.9909 2.92619 12.9998 2.89965 12.9998 2.87236C12.9998 2.84507 12.9909 2.81853 12.9745 2.79676C12.9581 2.77499 12.935 2.75918 12.9087 2.75173Z" fill="#003FFF"/></svg>';

export default class extends Controller {
  static targets = [
    "input",
    "sendButton",
    "messages",
    "error",
    "uploadButton",
    "assetThumbnail",
    "assetPreviewList",
  ];

  static values = {
    url: String,
    brandDescription: String,
    brandTone: String,
    businessType: String,
    avatarUrl: String,
    userGravatarUrl: String,
    websiteId: Number,
    environment: String,
    modal: Boolean,
    embed: Boolean,
    ctaType: String,
    ctaId: Number,
    variantId: Number,
    funnelTimeframe: String,
    funnelTimeframeStart: String,
    funnelTimeframeEnd: String,
    funnelBreakdownChat: { type: Boolean, default: false },
    playbookId: Number,
    playbookName: String,
    playbookTutorialTitle: String,
    playbookTutorialContent: String,
    relatedTemplateIds: String,
    relatedTemplateTheme: Object,
    relatedPlaybookIds: String,
    expandToModal: { type: Boolean, default: true },
    remixAiGated: { type: Boolean, default: false },
    actionsExhausted: { type: Boolean, default: false },
    actionsLimit: { type: Number, default: 0 },
    accountAgent: { type: Boolean, default: false },
  };

  connect() {
    this.messagesHistory = [];
    this.pendingAssetUrls = [];
    this.pendingAssetMetadata = {};
    this.storedHtmlImportAsset = null;
    this.storedMarkdownImportAsset = null;
    this.uploadInProgressCount = 0;
    this.richAttachmentInProgressCount = 0;
    this.inFlightUploadPromises = [];
    this.pickerUploadsInTransit = 0;
    this.submitting = false;
    this.suppressPlaybookRecommendationOptions = false;
    this.customTemplateTriggerPayloads = {};
    this.selectedCtaId = null;
    this.campaignHelpScope = null;
    this.cableConsumer = null;
    this.cableSubscription = null;
    this.uploadTypePopoverEl = null;
    this.boundHandleUploadTypePopoverClickOutside =
      this.handleUploadTypePopoverClickOutside.bind(this);
    // Create a single bound handler so we can properly remove the listener later
    if (!this.boundHandleKeydown) {
      this.boundHandleKeydown = this.handleKeydown.bind(this);
    }
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown);
    if (!this.boundAdjustInputHeight) {
      this.boundAdjustInputHeight = this.adjustInputHeight.bind(this);
    }
    this.inputTarget.addEventListener("input", this.boundAdjustInputHeight);
    if (!this.boundHandleInputPaste) {
      this.boundHandleInputPaste = this.handleInputPaste.bind(this);
    }
    this.inputTarget.addEventListener("paste", this.boundHandleInputPaste);
    this.adjustInputHeight();
    this.bindComposerDropEvents();

    if (this.hasMessagesTarget) {
      if (!this.boundHandleOptionClick) {
        this.boundHandleOptionClick = this.handleOptionClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleOptionClick,
        true
      );

      if (!this.boundHandleThumbnailPreviewClick) {
        this.boundHandleThumbnailPreviewClick =
          this.handleThumbnailPreviewClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleThumbnailPreviewClick
      );

      if (!this.boundHandleCustomTemplateTrigger) {
        this.boundHandleCustomTemplateTrigger =
          this.handleCustomTemplateTrigger.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleCustomTemplateTrigger
      );

      if (!this.boundHandleApplyExperiment) {
        this.boundHandleApplyExperiment = this.handleApplyExperiment.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleApplyExperiment
      );

      if (!this.boundHandleCampaignSearchInput) {
        this.boundHandleCampaignSearchInput =
          this.handleCampaignSearchInput.bind(this);
      }
      this.messagesTarget.addEventListener(
        "input",
        this.boundHandleCampaignSearchInput
      );
      if (!this.boundHandleCampaignSearchClick) {
        this.boundHandleCampaignSearchClick =
          this.handleCampaignSearchClick.bind(this);
      }
      this.messagesTarget.addEventListener(
        "click",
        this.boundHandleCampaignSearchClick
      );
    }

    if (this.modalValue) {
      this.boundHandleOpenEvent = this.handleOpenEvent.bind(this);
      document.addEventListener(
        "playbook-chat:open",
        this.boundHandleOpenEvent
      );
    }

    this.renderPrefillIfEmpty();

    if (this.isInsightsSidebarEmbed()) {
      this.setupInsightsSidebarHeightSync();
    }

    if (this.isFunnelBreakdownChat) {
      this.boundFunnelBreakdownVariantChanged =
        this.handleFunnelBreakdownVariantChanged.bind(this);
      document.addEventListener(
        "funnel-breakdown:variant-changed",
        this.boundFunnelBreakdownVariantChanged
      );
      this.boundFunnelBreakdownTimeframeChanged =
        this.handleFunnelBreakdownTimeframeChanged.bind(this);
      document.addEventListener(
        "funnel-breakdown:timeframe-changed",
        this.boundFunnelBreakdownTimeframeChanged
      );
    }

    if (this.hasAssetPreviewListTarget) {
      this.boundAssetPreviewListClick = this.onAssetPreviewListClick.bind(this);
      this.assetPreviewListTarget.addEventListener(
        "click",
        this.boundAssetPreviewListClick
      );
    }
  }

  teardownCable() {
    if (this.cableSubscription) {
      this.cableSubscription.unsubscribe();
      this.cableSubscription = null;
    }
    if (this.cableConsumer) {
      this.cableConsumer.disconnect();
      this.cableConsumer = null;
    }
    if (this.asyncTimeoutId) {
      clearTimeout(this.asyncTimeoutId);
      this.asyncTimeoutId = null;
    }
    this.rearmAsyncTimeout = null;
    if (this.chatLiveness) {
      this.chatLiveness.dispose();
      this.chatLiveness = null;
    }
  }

  armLivenessTimeout(onTimeout) {
    if (this.chatLiveness) this.chatLiveness.dispose();
    this.chatLiveness = createChatLiveness({
      timeoutMs: 60000,
      isActive: () =>
        this.hasSendButtonTarget && this.sendButtonTarget.disabled,
      onTimeout: () => {
        this.teardownCable();
        onTimeout();
      },
    });
    this.rearmAsyncTimeout = () =>
      this.chatLiveness && this.chatLiveness.bump();
  }

  disconnect() {
    this.teardownCable();
    this.hideUploadTypePopover();
    if (this.hasAssetPreviewListTarget && this.boundAssetPreviewListClick) {
      this.assetPreviewListTarget.removeEventListener(
        "click",
        this.boundAssetPreviewListClick
      );
      this.boundAssetPreviewListClick = null;
    }
    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }
    if (this.boundHandleKeydown) {
      this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown);
    }
    if (this.boundAdjustInputHeight && this.hasInputTarget) {
      this.inputTarget.removeEventListener(
        "input",
        this.boundAdjustInputHeight
      );
    }
    if (this.boundHandleInputPaste && this.hasInputTarget) {
      this.inputTarget.removeEventListener("paste", this.boundHandleInputPaste);
    }
    if (this.hasMessagesTarget && this.boundHandleOptionClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleOptionClick,
        true
      );
    }
    if (this.hasMessagesTarget && this.boundHandleThumbnailPreviewClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleThumbnailPreviewClick
      );
    }
    if (this.hasMessagesTarget && this.boundHandleCustomTemplateTrigger) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleCustomTemplateTrigger
      );
    }
    if (this.hasMessagesTarget && this.boundHandleApplyExperiment) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleApplyExperiment
      );
    }
    if (this.hasMessagesTarget && this.boundHandleCampaignSearchInput) {
      this.messagesTarget.removeEventListener(
        "input",
        this.boundHandleCampaignSearchInput
      );
    }
    if (this.hasMessagesTarget && this.boundHandleCampaignSearchClick) {
      this.messagesTarget.removeEventListener(
        "click",
        this.boundHandleCampaignSearchClick
      );
    }
    this.unbindComposerDropEvents();
    if (this.boundFunnelBreakdownVariantChanged) {
      document.removeEventListener(
        "funnel-breakdown:variant-changed",
        this.boundFunnelBreakdownVariantChanged
      );
      this.boundFunnelBreakdownVariantChanged = null;
    }
    if (this.boundFunnelBreakdownTimeframeChanged) {
      document.removeEventListener(
        "funnel-breakdown:timeframe-changed",
        this.boundFunnelBreakdownTimeframeChanged
      );
      this.boundFunnelBreakdownTimeframeChanged = null;
    }
    if (this.modalValue && this.boundHandleOpenEvent) {
      document.removeEventListener(
        "playbook-chat:open",
        this.boundHandleOpenEvent
      );
    }
    if (this.boundHandleEscape) {
      document.removeEventListener("keydown", this.boundHandleEscape);
      this.boundHandleEscape = null;
    }
    this.teardownInsightsSidebarHeightSync();
  }

  handleOpenEvent(event) {
    this.openModal();
    this.selectPrefillOption(event?.detail?.option);
  }

  selectPrefillOption(option) {
    if (!option || this.remixAiGatedValue) return;
    if ((this.messagesHistory || []).length > 0) return;
    if (this.planBlocksSending()) return;

    requestAnimationFrame(() => {
      const pill = Array.from(
        this.element.querySelectorAll(".playbook-chat-option")
      ).find((node) => node.dataset.option === option);
      if (!pill) return;
      pill.click();
    });
  }

  /** Open the AI chat modal. Call from anywhere via: document.dispatchEvent(new CustomEvent('playbook-chat:open')) */
  openModal() {
    if (this.modalValue) {
      this.element.classList.remove("hidden");
      this.element.classList.add("flex", "items-center", "justify-center");
      document.body.classList.add("modal-open");
      this.boundHandleEscape = this.handleEscape.bind(this);
      document.addEventListener("keydown", this.boundHandleEscape);
      requestAnimationFrame(() => {
        this.inputTarget?.focus();
        this.adjustInputHeight();
      });
    } else if (this.embedValue) {
      this.element.classList.add("playbook-chat-modal-active");
      document.body.classList.add("modal-open");
      this.boundHandleEscape = this.handleEscape.bind(this);
      document.addEventListener("keydown", this.boundHandleEscape);
      requestAnimationFrame(() => {
        this.inputTarget?.focus();
        this.adjustInputHeight();
      });
    }
  }

  adjustInputHeight() {
    if (!this.hasInputTarget) return;
    const el = this.inputTarget;
    if (el.tagName !== "TEXTAREA") return;
    const maxPx = COMPOSER_MAX_HEIGHT_PX;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
    el.style.overflowY = el.scrollHeight > maxPx ? "auto" : "hidden";
  }

  closeModal() {
    if (this.modalValue) {
      this.element.classList.add("hidden");
      this.element.classList.remove("flex", "items-center", "justify-center");
      document.body.classList.remove("modal-open");
      if (this.boundHandleEscape) {
        document.removeEventListener("keydown", this.boundHandleEscape);
        this.boundHandleEscape = null;
      }
      // Hide empty thumbnail when closing modal if no asset was uploaded
      if ((this.pendingAssetUrls || []).length === 0) {
        this.clearAssetPreview();
      }
    } else if (this.embedValue) {
      this.element.classList.remove("playbook-chat-modal-active");
      document.body.classList.remove("modal-open");
      if (this.boundHandleEscape) {
        document.removeEventListener("keydown", this.boundHandleEscape);
        this.boundHandleEscape = null;
      }
      this.resetEmbedChat();
    }
  }

  /** Reset embed chat state when closing the popup so the next open starts fresh. */
  resetEmbedChat() {
    this.messagesHistory = [];
    this.pendingAssetUrls = [];
    this.pendingAssetMetadata = {};
    this.uploadInProgressCount = 0;
    this.richAttachmentInProgressCount = 0;
    this.inFlightUploadPromises = [];
    this.pickerUploadsInTransit = 0;
    this.submitting = false;
    this.suppressPlaybookRecommendationOptions = false;
    this.detectedAssetTheme = null;
    if (this.hasMessagesTarget) {
      this.messagesTarget.innerHTML = "";
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
      this.inputTarget.placeholder = this.defaultInputPlaceholder;
      this.adjustInputHeight();
    }
    this.clearAssetPreview();
    this.clearError();
    this.setLoading(false);
    this.renderPrefillIfEmpty();
  }

  /** Show upload source chooser popover near the + button. */
  openUploadPicker(event) {
    if (this.actionsExhaustedValue) {
      this.showRemixAiActionsExhaustedModal();
      return;
    }
    // Free plans may upload assets so we can capture custom-template intent;
    // submit still feature-gates LLM / generation.
    event?.preventDefault?.();
    this.toggleUploadTypePopover();
  }

  toggleUploadTypePopover() {
    if (this.uploadTypePopoverEl) {
      this.hideUploadTypePopover();
      return;
    }
    this.showUploadTypePopover();
  }

  showUploadTypePopover() {
    if (!this.hasUploadButtonTarget) return;
    this.hideUploadTypePopover();

    const rect = this.uploadButtonTarget.getBoundingClientRect();
    const imageUploadDisabled = this.remainingImageUploadSlots() <= 0;
    const imageChoiceClass = imageUploadDisabled
      ? "w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-400 flex items-center gap-2 cursor-not-allowed"
      : "w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-800 hover:bg-slate-50 flex items-center gap-2";
    const imageChoiceTitle = imageUploadDisabled
      ? `You can attach up to ${MAX_IMAGE_ATTACHMENTS} images`
      : "Upload an image";
    const popover = document.createElement("div");
    popover.setAttribute("data-playbook-upload-type-popover", "true");
    popover.className =
      "z-[10010] fixed bg-white rounded-xl shadow-lg overflow-hidden";
    popover.style.visibility = "hidden";
    popover.innerHTML = `
      <button type="button" data-upload-choice="image"${
        imageUploadDisabled ? " disabled" : ""
      } class="${imageChoiceClass}" title="${imageChoiceTitle}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-black flex-shrink-0">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
          <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor"/>
          <path d="M5.5 16L10 12L13 14.5L16.5 11L18.5 13V16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Upload an image</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" class="text-black ml-auto flex-shrink-0">
          <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="h-px bg-slate-200 mx-3"></div>
      <button type="button" data-upload-choice="html" class="w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-800 hover:bg-slate-50 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-black flex-shrink-0">
          <path d="M14 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V7L14 2Z" stroke="currentColor" stroke-width="1.8"/>
          <path d="M14 2V7H19" stroke="currentColor" stroke-width="1.8"/>
          <path d="M9 12H15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        <span>Upload HTML</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" class="text-black ml-auto flex-shrink-0">
          <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="h-px bg-slate-200 mx-3"></div>
      <button type="button" data-upload-choice="md" class="w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-800 hover:bg-slate-50 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-black flex-shrink-0">
          <rect x="2.5" y="5.5" width="19" height="13" rx="2" stroke="currentColor" stroke-width="1.8"/>
          <path d="M5.5 15V9L8 12L10.5 9V15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 9V14.5M16 14.5L13.75 12.25M16 14.5L18.25 12.25" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Upload MD</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" class="text-black ml-auto flex-shrink-0">
          <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="h-px bg-slate-200 mx-3"></div>
      <button type="button" data-upload-choice="url" class="w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-800 hover:bg-slate-50 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-black flex-shrink-0">
          <path d="M10 14L14 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M8.5 7.5L6.5 9.5C5.1 10.9 5.1 13.1 6.5 14.5C7.9 15.9 10.1 15.9 11.5 14.5L13.5 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          <path d="M15.5 16.5L17.5 14.5C18.9 13.1 18.9 10.9 17.5 9.5C16.1 8.1 13.9 8.1 12.5 9.5L10.5 11.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
        <span>Website URL</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" class="text-black ml-auto flex-shrink-0">
          <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div data-upload-url-row="true" class="hidden px-3 pb-3">
        <div class="flex items-center gap-2 border border-slate-200 rounded-lg px-2 py-2">
          <input type="url" data-upload-url-input="true" placeholder="https://example.com" class="flex-1 min-w-0 border-0 outline-none text-sm text-gray-700 bg-transparent" />
          <button type="button" data-upload-url-submit="true" class="border-0 bg-transparent p-0 text-black flex items-center justify-center cursor-pointer" aria-label="Attach website URL" title="Attach website URL">
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M12 5L19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    popover.addEventListener("click", (e) => {
      const choice = e.target.closest("[data-upload-choice]")?.dataset
        ?.uploadChoice;
      if (choice === "image") {
        this.hideUploadTypePopover();
        this.openImageUploadPicker();
        return;
      }
      if (choice === "html") {
        this.hideUploadTypePopover();
        this.openHtmlFilePicker();
        return;
      }
      if (choice === "md") {
        this.hideUploadTypePopover();
        this.openMarkdownFilePicker();
        return;
      }
      if (choice === "url") {
        const row = popover.querySelector('[data-upload-url-row="true"]');
        const input = popover.querySelector('[data-upload-url-input="true"]');
        if (row) row.classList.remove("hidden");
        input?.focus();
        return;
      }
      const submit = e.target.closest("[data-upload-url-submit]");
      if (submit) {
        const input = popover.querySelector('[data-upload-url-input="true"]');
        this.submitWebsiteUrlAttachment(input?.value || "");
        this.hideUploadTypePopover();
      }
    });
    popover.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const input = e.target.closest("[data-upload-url-input]");
      if (!input) return;
      e.preventDefault();
      this.submitWebsiteUrlAttachment(input.value || "");
      this.hideUploadTypePopover();
    });

    document.body.appendChild(popover);
    const popRect = popover.getBoundingClientRect();
    const preferredTop = rect.top - popRect.height - 8;
    const top = preferredTop < 8 ? rect.bottom + 8 : preferredTop;
    const centeredLeft = rect.left + rect.width / 2 - popRect.width / 2;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - popRect.width - 8, centeredLeft + 50)
    );
    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
    popover.style.visibility = "visible";
    this.uploadTypePopoverEl = popover;
    document.addEventListener(
      "pointerdown",
      this.boundHandleUploadTypePopoverClickOutside
    );
  }

  hideUploadTypePopover() {
    if (this.uploadTypePopoverEl?.parentNode) {
      this.uploadTypePopoverEl.parentNode.removeChild(this.uploadTypePopoverEl);
    }
    this.uploadTypePopoverEl = null;
    document.removeEventListener(
      "pointerdown",
      this.boundHandleUploadTypePopoverClickOutside
    );
  }

  handleUploadTypePopoverClickOutside(event) {
    if (!this.uploadTypePopoverEl) return;
    if (this.uploadTypePopoverEl.contains(event.target)) return;
    if (
      this.hasUploadButtonTarget &&
      this.uploadButtonTarget.contains(event.target)
    ) {
      return;
    }
    this.hideUploadTypePopover();
  }

  /** Open Filestack image picker; image upload pipeline stays unchanged. */
  openImageUploadPicker() {
    if (typeof window.uploads === "undefined" || !window.uploads.select) return;
    const remaining = this.remainingImageUploadSlots();
    if (remaining <= 0) {
      this.showError(`You can attach up to ${MAX_IMAGE_ATTACHMENTS} images.`);
      return;
    }
    this.uploadStarted = false;
    const websiteId = this.websiteIdValue;
    const env = (this.environmentValue || "production").toLowerCase();
    const path = `/websites/${websiteId}/remix/`;
    window.uploads.select("callback", {
      environment: env,
      path,
      accept: [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ],
      website_id: websiteId,
      forceNsfwCheck: true,
      skipProcessImage: true,
      maxFiles: remaining,
      onUploadStarted: () => {
        this.uploadStarted = true;
        this.uploadInProgressCount += 1;
        this.pickerUploadsInTransit = (this.pickerUploadsInTransit | 0) + 1;
        this.showAssetPreviewPlaceholder();
      },
      onUploadFinished: () => {
        this.pickerUploadsInTransit = Math.max(
          0,
          (this.pickerUploadsInTransit | 0) - 1
        );
      },
      onUploadFailed: (reason) => {
        this.finishUpload();
        this.updateAssetPreview();
        this.showError(uploadFailureMessage(new Error(reason)));
      },
      onClose: () => {
        this.uploadInProgressCount = Math.max(
          0,
          this.uploadInProgressCount - (this.pickerUploadsInTransit | 0)
        );
        this.pickerUploadsInTransit = 0;
        this.clearUploadWaitMessageIfIdle();
        if (!this.uploadStarted) {
          if ((this.pendingAssetUrls || []).length === 0) {
            this.clearAssetPreview();
            return;
          }
        }
        this.updateAssetPreview();
      },
      callback: (fileUrl, fileMeta = {}) => {
        this.finishUpload();
        this.addPendingAssetUrl(fileUrl, fileMeta);
        this.updateAssetPreview();
        if (this.isImageAsset(fileUrl)) {
          this.detectAssetTheme(fileUrl);
        }
      },
    });
  }

  /** Open native file picker for HTML, parse client-side, and attach content. */
  openHtmlFilePicker() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".html,.htm,text/html";
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener("change", async () => {
      try {
        const file = input.files?.[0];
        if (!file) return;
        this.attachHtmlDocument(
          await file.text(),
          file.name || "uploaded.html"
        );
      } finally {
        if (input.parentNode) input.parentNode.removeChild(input);
      }
    });
    input.click();
  }

  supportsImageAttachments() {
    return this.hasAssetThumbnailTarget && this.websiteIdValue > 0;
  }

  handleInputPaste(event) {
    const files = Array.from(event.clipboardData?.files || []);
    const hasImportFiles =
      this.supportsImageAttachments() &&
      files.some((file) => isImageFile(file) || isAiImportTextFile(file));
    const attachment = attachmentFromClipboard(event.clipboardData);
    const textAttachment =
      attachment &&
      (attachment.type === "html" || attachment.type === "markdown")
        ? attachment
        : null;

    if (hasImportFiles) {
      event.preventDefault();
      if (this.actionsExhaustedValue) {
        this.showRemixAiActionsExhaustedModal();
        return;
      }
      const filePromise = this.trackInFlightUpload(
        this.attachClipboardFiles(files)
      );
      if (textAttachment && this.remainingRichAttachmentSlots() > 0) {
        this.attachClipboardTextAttachment(textAttachment);
      }
      return filePromise;
    }

    if (!textAttachment) return;

    event.preventDefault();
    if (this.actionsExhaustedValue) {
      this.showRemixAiActionsExhaustedModal();
      return;
    }
    this.attachClipboardTextAttachment(textAttachment);
  }

  attachClipboardTextAttachment(attachment) {
    if (this.remainingRichAttachmentSlots() <= 0) {
      const { maxRichAttachments } = this.getAttachmentPromptLimits();
      this.showError(
        `You can attach up to ${maxRichAttachments} HTML or Markdown files.`
      );
      return;
    }
    this.clearError();
    if (attachment.type === "html") {
      this.attachHtmlDocument(attachment.content);
      return;
    }
    this.attachMarkdownDocument(attachment.content);
  }

  attachClipboardFiles(files) {
    const remaining = this.remainingImageUploadSlots();
    const { accepted, omitted, oversized } = takeImageFiles(files, remaining);
    const dropMessage = imageDropLimitMessage({
      omitted,
      oversized,
      limitLabel: `${MAX_IMAGE_ATTACHMENTS} images`,
    });
    const textFiles = Array.from(files || []).filter(isAiImportTextFile);
    const remainingRich = this.remainingRichAttachmentSlots();
    const textToRead = textFiles.slice(0, remainingRich);
    const htmlCapMessage =
      textFiles.length > remainingRich
        ? `You can attach up to ${
            this.getAttachmentPromptLimits().maxRichAttachments
          } HTML or Markdown files.`
        : "";
    const message = [dropMessage, htmlCapMessage].filter(Boolean).join(" ");
    if (message) {
      this.showError(message);
    } else {
      this.clearError();
    }
    if (accepted.length > 0) {
      this.uploadDroppedImageFiles(accepted);
    }

    return Promise.all(
      textToRead.map(async (file) => {
        const name = String(file.name || "").toLowerCase();
        try {
          const raw = await readClipboardFileText(file);
          if (
            name.endsWith(".md") ||
            name.endsWith(".markdown") ||
            file.type === "text/markdown"
          ) {
            this.attachMarkdownDocument(raw, file.name || "pasted.md");
            return;
          }
          this.attachHtmlDocument(raw, file.name || "pasted.html");
        } catch (_error) {
          this.showError("Unable to attach that file. Try the + button.");
        }
      })
    );
  }

  bindComposerDropEvents() {
    if (!this.boundHandleComposerDragOver) {
      this.boundHandleComposerDragOver = this.handleComposerDragOver.bind(this);
    }
    if (!this.boundHandleComposerDrop) {
      this.boundHandleComposerDrop = this.handleComposerDrop.bind(this);
    }
    this.element.addEventListener("dragover", this.boundHandleComposerDragOver);
    this.element.addEventListener(
      "dragenter",
      this.boundHandleComposerDragOver
    );
    this.element.addEventListener("drop", this.boundHandleComposerDrop);
  }

  unbindComposerDropEvents() {
    if (this.boundHandleComposerDragOver) {
      this.element.removeEventListener(
        "dragover",
        this.boundHandleComposerDragOver
      );
      this.element.removeEventListener(
        "dragenter",
        this.boundHandleComposerDragOver
      );
    }
    if (this.boundHandleComposerDrop) {
      this.element.removeEventListener("drop", this.boundHandleComposerDrop);
    }
  }

  handleComposerDragOver(event) {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect =
      this.supportsImageAttachments() &&
      (dataTransferHasImages(event.dataTransfer) ||
        htmlOrMarkdownFilesFromDataTransfer(event.dataTransfer).length > 0)
        ? "copy"
        : "none";
  }

  handleComposerDrop(event) {
    if (!dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    if (!this.supportsImageAttachments()) return;
    const files = Array.from(event.dataTransfer?.files || []);
    const textFiles = htmlOrMarkdownFilesFromDataTransfer(event.dataTransfer);
    const remaining = this.remainingImageUploadSlots();
    const { accepted, omitted, oversized, unsupported } = takeImageFiles(
      files,
      remaining
    );
    const dropMessage = imageDropLimitMessage({
      omitted,
      oversized,
      unsupported: Math.max(0, unsupported - textFiles.length),
      limitLabel: `${MAX_IMAGE_ATTACHMENTS} images`,
    });
    const remainingRich = this.remainingRichAttachmentSlots();
    const textToAttach = textFiles.slice(0, remainingRich);
    const htmlCapMessage =
      textFiles.length > remainingRich
        ? `You can attach up to ${
            this.getAttachmentPromptLimits().maxRichAttachments
          } HTML or Markdown files.`
        : "";
    const message = [dropMessage, htmlCapMessage].filter(Boolean).join(" ");
    if (message) {
      this.showError(message);
    } else {
      this.clearError();
    }
    if (accepted.length === 0 && textToAttach.length === 0) return;
    if (this.actionsExhaustedValue) {
      this.showRemixAiActionsExhaustedModal();
      return;
    }
    if (accepted.length > 0) this.uploadDroppedImageFiles(accepted);
    textToAttach.forEach((file) =>
      this.trackInFlightUpload(this.attachDroppedTextFile(file))
    );
  }

  async attachDroppedTextFile(file) {
    this.richAttachmentInProgressCount =
      (this.richAttachmentInProgressCount | 0) + 1;
    try {
      const raw = unwrapBundlerHtml(await readClipboardFileText(file));
      const isMarkdown =
        /\.(md|markdown)$/i.test(file.name || "") ||
        String(file.type || "").toLowerCase() === "text/markdown";
      if (isMarkdown) {
        this.attachMarkdownDocument(raw, file.name || "uploaded.md");
      } else {
        this.attachHtmlDocument(raw, file.name || "uploaded.html");
      }
    } catch (_error) {
      this.showError("Couldn't attach that file. Please try again.");
    } finally {
      this.richAttachmentInProgressCount = Math.max(
        0,
        (this.richAttachmentInProgressCount | 0) - 1
      );
    }
  }

  uploadDroppedImageFiles(files) {
    const websiteId = this.websiteIdValue;
    const env = (this.environmentValue || "production").toLowerCase();
    const path = `/websites/${websiteId}/remix/`;
    files.forEach((file) => {
      this.uploadInProgressCount += 1;
      this.showAssetPreviewPlaceholder();
      const upload = uploadLocalImageFile({
        file,
        environment: env,
        path,
        websiteId,
        forceNsfwCheck: true,
        skipProcessImage: true,
        onUploadStarted: () => {},
        callback: (fileUrl, fileMeta = {}) => {
          this.finishUpload();
          this.addPendingAssetUrl(fileUrl, fileMeta);
          this.updateAssetPreview();
          if (this.isImageAsset(fileUrl)) {
            this.detectAssetTheme(fileUrl);
          }
        },
      }).catch((error) => {
        this.finishUpload();
        this.updateAssetPreview();
        this.showError(uploadFailureMessage(error));
      });
      this.trackInFlightUpload(upload);
    });
  }

  finishUpload() {
    if (this.uploadInProgressCount > 0) {
      this.uploadInProgressCount -= 1;
    }
    this.clearUploadWaitMessageIfIdle();
  }

  clearUploadWaitMessageIfIdle() {
    if (
      this.uploadInProgressCount === 0 &&
      this.hasErrorTarget &&
      this.errorTarget.textContent === UPLOAD_WAIT_MESSAGE
    ) {
      this.clearError();
    }
  }

  uploadsBlockSending() {
    if (!(this.uploadInProgressCount > 0)) return false;
    this.showError(UPLOAD_WAIT_MESSAGE);
    return true;
  }

  planBlocksSending() {
    if (this.actionsExhaustedValue) {
      this.showRemixAiActionsExhaustedModal();
      return true;
    }
    if (this.remixAiGatedValue && this.isAccountAgent) {
      this.showRemixAiAccountAgentUpgradeModal();
      return true;
    }
    return false;
  }

  attachHtmlDocument(rawText, filename) {
    const html = unwrapBundlerHtml(String(rawText || "").trim());
    const { maxHtmlCharsPerFile } = this.getAttachmentPromptLimits();
    const truncated = html.slice(0, maxHtmlCharsPerFile);
    const scriptSource = this.extractScriptSourceFromHtml(html);
    const ref = `local-html://${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    this.addPendingAssetUrl(ref, {
      mimetype: "text/html",
      filename: filename || this.pastedHtmlFilename(html),
      is_html: true,
      html_content: truncated,
      script_source: scriptSource || "",
      html_truncated: html.length > truncated.length,
    });
    this.updateAssetPreview();
  }

  attachMarkdownDocument(rawText, filename) {
    const raw = String(rawText || "");
    const { maxHtmlCharsPerFile } = this.getAttachmentPromptLimits();
    const truncated = raw.slice(0, maxHtmlCharsPerFile);
    const ref = `local-md://${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    this.addPendingAssetUrl(ref, {
      mimetype: "text/markdown",
      filename: filename || "pasted.md",
      is_markdown: true,
      markdown_content: truncated,
      markdown_truncated: raw.length > truncated.length,
    });
    this.updateAssetPreview();
  }

  pastedHtmlFilename(html) {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const title = (
        doc.querySelector("title")?.textContent ||
        doc.querySelector("h1, h2")?.textContent ||
        ""
      ).trim();
      const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      if (slug) return `${slug}.html`;
    } catch (_e) {
      // fall through to the generic name
    }
    return "pasted.html";
  }

  /** Open native file picker for Markdown funnel specs and attach content. */
  openMarkdownFilePicker() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,.markdown,text/markdown";
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener("change", async () => {
      try {
        const file = input.files?.[0];
        if (!file) return;
        this.attachMarkdownDocument(
          await file.text(),
          file.name || "uploaded.md"
        );
      } finally {
        if (input.parentNode) input.parentNode.removeChild(input);
      }
    });
    input.click();
  }

  submitWebsiteUrlAttachment(rawValue) {
    const normalized = this.normalizeWebsiteUrl(rawValue);
    if (!normalized) return;
    const ref = `attached-url://${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    this.addPendingAssetUrl(ref, {
      mimetype: "text/uri-list",
      filename: normalized,
      is_url: true,
      website_url: normalized,
    });
    this.updateAssetPreview();
  }

  normalizeWebsiteUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const withScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(raw)
      ? raw
      : `https://${raw}`;
    try {
      const parsed = new URL(withScheme);
      if (!["http:", "https:"].includes(parsed.protocol)) return null;
      return parsed.toString();
    } catch (_e) {
      return null;
    }
  }

  /** Rebuild preview row (thumbnails + uploading placeholders). */
  showAssetPreviewPlaceholder() {
    this.updateAssetPreview();
  }

  escapeCssUrl(url) {
    return url.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  clearAssetPreview() {
    this.pendingAssetUrls = [];
    this.pendingAssetMetadata = {};
    this.uploadInProgressCount = 0;
    this.richAttachmentInProgressCount = 0;
    this.inFlightUploadPromises = [];
    this.pickerUploadsInTransit = 0;
    if (this.hasAssetThumbnailTarget) {
      this.assetThumbnailTarget.classList.add("hidden");
      this.assetThumbnailTarget.style.display = "none";
    }
    if (this.hasAssetPreviewListTarget) {
      this.assetPreviewListTarget.innerHTML = "";
    }
  }

  removeAsset() {
    this.detectedAssetTheme = null;
    this.clearAssetPreview();
  }

  onAssetPreviewListClick(event) {
    const btn = event.target.closest("[data-playbook-remove-pending-index]");
    if (!btn || !this.hasAssetPreviewListTarget) return;
    if (!this.assetPreviewListTarget.contains(btn)) return;
    event.preventDefault();
    event.stopPropagation();

    const index = parseInt(
      btn.getAttribute("data-playbook-remove-pending-index"),
      10
    );
    if (!Number.isFinite(index) || index < 0) return;

    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    if (index >= urls.length) return;

    const removedUrl = urls[index];
    urls.splice(index, 1);
    this.pendingAssetUrls = urls;
    if (removedUrl && this.pendingAssetMetadata) {
      delete this.pendingAssetMetadata[removedUrl];
    }

    if (urls.length === 0) {
      this.detectedAssetTheme = null;
    } else {
      this.detectedAssetTheme = null;
      const firstImageUrl = this.findFirstPendingImageUrl();
      if (firstImageUrl) {
        this.detectAssetTheme(firstImageUrl);
      }
    }

    this.updateAssetPreview();
  }

  async detectAssetTheme(imageUrl) {
    if (!imageUrl) return;
    try {
      console.log("[detectAssetTheme] Starting extraction for:", imageUrl);
      const colors = await this.extractColorsFromImage(imageUrl);
      console.log("[detectAssetTheme] Extracted colors:", colors);
      if (!colors || !this.pendingAssetUrls.includes(imageUrl)) {
        console.log(
          "[detectAssetTheme] Skipped — colors:",
          colors,
          "pendingAssetUrls includes image:",
          this.pendingAssetUrls.includes(imageUrl)
        );
        return;
      }

      const isDark = this.relativeLuminance(colors.background) < 0.4;
      const textColor = isDark ? "#ffffff" : "#000000";

      this.detectedAssetTheme = {
        "background-color": colors.background,
        "button-color": colors.accent,
        "accent-color": colors.accent,
        "heading-font-color": textColor,
        "paragraph-font-color": textColor,
      };
      console.log("[detectAssetTheme] Theme set:", this.detectedAssetTheme);
    } catch (e) {
      console.error("[detectAssetTheme] Error:", e);
    }
  }

  extractColorsFromImage(imageUrl) {
    let corsUrl = imageUrl;
    const filestackMatch = imageUrl.match(
      /cdn\.filestackcontent\.com\/([A-Za-z0-9]+)/
    );
    if (filestackMatch) {
      corsUrl = `https://cdn.filestackcontent.com/resize=width:64,height:64,fit:crop/output=format:png/${filestackMatch[1]}`;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const size = Math.min(img.width, 64);
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, size, size);
          const data = ctx.getImageData(0, 0, size, size).data;

          const buckets = {};
          for (let i = 0; i < data.length; i += 16) {
            const r = Math.round(data[i] / 32) * 32;
            const g = Math.round(data[i + 1] / 32) * 32;
            const b = Math.round(data[i + 2] / 32) * 32;
            const key = `${r},${g},${b}`;
            buckets[key] = (buckets[key] || 0) + 1;
          }

          const sorted = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
          const bgParts = sorted[0][0].split(",").map(Number);
          const bgHex = this.rgbToHex(bgParts[0], bgParts[1], bgParts[2]);

          let accentHex = "#0D42E4";
          for (let i = 1; i < sorted.length; i++) {
            const parts = sorted[i][0].split(",").map(Number);
            if (this.colorDistance(bgParts, parts) > 80) {
              accentHex = this.rgbToHex(parts[0], parts[1], parts[2]);
              break;
            }
          }

          resolve({ background: bgHex, accent: accentHex });
        } catch (_e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = corsUrl;
    });
  }

  rgbToHex(r, g, b) {
    return (
      "#" +
      [r, g, b]
        .map((c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, "0"))
        .join("")
    );
  }

  colorDistance(a, b) {
    return Math.sqrt(
      (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2
    );
  }

  relativeLuminance(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  renderPrefillIfEmpty() {
    if (!this.hasMessagesTarget) return;

    const hasDomMessages = this.messagesTarget.children.length > 0;
    const hasHistoryMessages = (this.messagesHistory || []).length > 0;
    if (hasDomMessages || hasHistoryMessages) return;

    this.appendAssistantMessage(
      "",
      [],
      false,
      { options: this.prefillOptions },
      false
    );
    if (this.isInsightsSidebarEmbed()) {
      this.scheduleInsightsSidebarHeightSync();
    }
  }

  refreshPrefillIfEmpty() {
    if (!this.hasMessagesTarget) return;
    if ((this.messagesHistory || []).length > 0) return;
    if (this.messagesTarget.querySelector(".flex.justify-end")) return;

    this.messagesTarget.innerHTML = "";
    this.renderPrefillIfEmpty();
  }

  get hasPendingAssets() {
    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    return urls.length > 0 || (this.uploadInProgressCount | 0) > 0;
  }

  isGenerateTemplatePrefillOption(text) {
    const normalized = String(text || "")
      .trim()
      .toLowerCase();
    return [
      GENERATE_CUSTOM_TEMPLATE_PREFILL_OPTION.toLowerCase(),
      GENERATE_CUSTOM_TEMPLATE_PILL_OPTION.toLowerCase(),
      GENERATE_TEMPLATE_LABEL.toLowerCase(),
    ].includes(normalized);
  }

  isHighEffortCustomTemplateImport({ imageUrl, imageUrls } = {}) {
    if (String(imageUrl || "").trim()) return true;
    return (
      Array.isArray(imageUrls) &&
      imageUrls.some((url) => typeof url === "string" && url.trim())
    );
  }

  customTemplateActionNoteHtml(highEffort, hasHtml = false) {
    const text = highEffort
      ? `Uses ${HIGH_EFFORT_IMPORT_ACTIONS} actions`
      : hasHtml
      ? `Uses up to ${HIGH_EFFORT_IMPORT_ACTIONS} actions`
      : `Uses ${LOW_EFFORT_IMPORT_ACTIONS} actions`;
    return `<p class="mt-1.5 mb-0 text-sm text-gray-500 leading-snug">${text}</p>`;
  }

  customTemplateTriggerButtonHtml({ triggerId, highEffort, hasHtml = false }) {
    return `<div class="inline-flex flex-col items-start mt-2"><button type="button" class="inline-block px-4 py-2 rounded-full bg-white border border-solid border-blue-400 hover:border-blue-500 text-blue-600 hover:text-blue-700 font-semibold text-xl cursor-pointer transition-colors" data-custom-template-trigger data-custom-template-trigger-id="${this.escapeHtml(
      triggerId
    )}">${GENERATE_TEMPLATE_LABEL} →</button>${this.customTemplateActionNoteHtml(
      highEffort,
      hasHtml
    )}</div>`;
  }

  storeCustomTemplateTriggerPayload(payload) {
    if (!this.customTemplateTriggerPayloads) {
      this.customTemplateTriggerPayloads = {};
    }
    const triggerId = `ct-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    this.customTemplateTriggerPayloads[triggerId] = payload;
    return triggerId;
  }

  resolveCustomTemplateTriggerPayload(btn) {
    const triggerId = btn?.dataset?.customTemplateTriggerId;
    if (triggerId && this.customTemplateTriggerPayloads?.[triggerId]) {
      return this.customTemplateTriggerPayloads[triggerId];
    }

    const encoded = btn?.dataset?.customTemplateTrigger;
    if (!encoded) return null;

    return JSON.parse(decodeURIComponent(encoded));
  }

  extractScriptSourceFromHtml(htmlContent) {
    if (!htmlContent || typeof htmlContent !== "string") return null;

    try {
      const doc = new DOMParser().parseFromString(htmlContent, "text/html");
      const script = Array.from(doc.querySelectorAll("script"))
        .map((node) => node.textContent || "")
        .join("\n\n")
        .trim();
      return script || null;
    } catch (_e) {
      return null;
    }
  }

  customTemplatePillHtml({ option, variant = "recommendation" }) {
    const safeOption = this.escapeHtml(option);
    const showSparkle = variant === "recommendation";
    const label = showSparkle
      ? `${GENERATE_TEMPLATE_SPARKLE_ICON}${this.escapeHtml(
          GENERATE_TEMPLATE_LABEL
        )}`
      : this.escapeHtml(GENERATE_TEMPLATE_LABEL);
    if (variant === "prefill") {
      return `<button type="button" data-action="click->playbook-chat#submitPill" data-option="${safeOption}" class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl transition-colors" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">${label}</button>`;
    }
    return `<button type="button" data-action="click->playbook-chat#submitPill" data-option="${safeOption}" class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl transition-colors cursor-pointer bg-transparent border border-blue-400 hover:border-blue-300 text-blue-500 hover:text-blue-600">${label}</button>`;
  }

  get prefillOptions() {
    if (this.hasPendingAssets) {
      return [GENERATE_CUSTOM_TEMPLATE_PREFILL_OPTION];
    }

    return this.basePrefillOptions;
  }

  recommendationOptionsForMessage() {
    if (this.hasPendingAssets || this.suppressPlaybookRecommendationOptions) {
      return [GENERATE_CUSTOM_TEMPLATE_PILL_OPTION];
    }

    if (this.isAccountAgent) {
      return ["Recommend playbooks for another website"];
    }

    if (this.remixAiGatedValue) {
      return ["Recommend another template"];
    }

    return ["Recommend another template", GENERATE_CUSTOM_TEMPLATE_PILL_OPTION];
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (this.submitting) return;
      this.submit();
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeModal();
    }
  }

  /** Show upgrade modal when AI actions are exhausted. */
  showRemixAiActionsExhaustedModal(event) {
    if (
      typeof window.subscriptions === "undefined" ||
      !window.subscriptions.upgrade_modal
    ) {
      return;
    }
    const limit = this.actionsLimitValue || 100;
    const planType =
      document.getElementById("upgrade")?.getAttribute("data-plan-type") || "";
    const freePlan = String(planType).trim() === "free";
    const ev =
      event && typeof event.preventDefault === "function"
        ? event
        : { preventDefault() {} };
    window.subscriptions.upgrade_modal(
      ev,
      false,
      freePlan
        ? `continue with ConvertFlow AI after using all ${limit} included AI actions.`
        : `continue with ConvertFlow AI after using all ${limit} actions this month.`,
      remixAiUpgradePlan({}),
      "Triggered feature CTA – Remix AI actions limit",
      false,
      "x3rs6cvrwi"
    );
  }

  /** Show upgrade modal when legacy plans try to use the account AI agent. */
  showRemixAiAccountAgentUpgradeModal(event) {
    if (
      typeof window.subscriptions === "undefined" ||
      !window.subscriptions.upgrade_modal
    ) {
      return;
    }
    const ev =
      event && typeof event.preventDefault === "function"
        ? event
        : { preventDefault() {} };
    window.subscriptions.upgrade_modal(
      ev,
      false,
      "chat with ConvertFlow AI across your websites.",
      remixAiUpgradePlan({}),
      "Triggered feature CTA – Remix AI",
      false,
      "x3rs6cvrwi"
    );
  }

  /** Show upgrade modal for paid Remix AI features (uploads / custom templates). */
  showRemixAiPagesUpgradeModal(event) {
    if (
      typeof window.subscriptions === "undefined" ||
      !window.subscriptions.upgrade_modal
    ) {
      return;
    }
    const ev =
      event && typeof event.preventDefault === "function"
        ? event
        : { preventDefault() {} };
    window.subscriptions.upgrade_modal(
      ev,
      false,
      "use ConvertFlow AI to build custom templates for you.",
      remixAiUpgradePlan({}),
      "Triggered feature CTA – Remix AI custom templates",
      false,
      "x3rs6cvrwi"
    );
  }

  closeModalOnBackdrop(event) {
    const isOpen = this.modalValue
      ? !this.element.classList.contains("hidden")
      : this.element.classList.contains("playbook-chat-modal-active");
    if (!isOpen) return;

    const content = this.element.querySelector("[data-playbook-chat-content]");
    if (content && !content.contains(event.target)) {
      this.closeModal();
    }
  }

  insightsPrefillPromptFor(option) {
    if (this.isFunnelBreakdownChat) {
      return FUNNEL_BREAKDOWN_PREFILL_PROMPTS[option] || option;
    }

    if (this.isAccountAgent) {
      return ACCOUNT_AGENT_PREFILL_PROMPTS[option] || option;
    }

    if (this.isOnWebsitesInsights) {
      return (
        INSIGHTS_PREFILL_PROMPTS[option] ||
        WEBSITE_PREFILL_PROMPTS[option] ||
        option
      );
    }

    return WEBSITE_PREFILL_PROMPTS[option] || option;
  }

  handleFunnelBreakdownVariantChanged(event) {
    const nextVariantId = Number(event?.detail?.variantId);
    if (!Number.isFinite(nextVariantId) || nextVariantId <= 0) return;
    this.variantIdValue = nextVariantId;
  }

  handleFunnelBreakdownTimeframeChanged(event) {
    const timeframe = String(event?.detail?.timeframe || "").trim();
    if (!timeframe) return;
    this.funnelTimeframeValue = timeframe;
    this.funnelTimeframeStartValue = event?.detail?.startDate
      ? String(event.detail.startDate)
      : "";
    this.funnelTimeframeEndValue = event?.detail?.endDate
      ? String(event.detail.endDate)
      : "";
  }

  async submit(overrides = {}) {
    if (this.uploadsBlockSending()) return;

    const attachmentOnlyPrompt =
      (this.pendingAssetUrls || []).length > 0 ? "Use the attached files." : "";
    const displayText =
      (overrides.displayText ?? this.inputTarget.value).trim() ||
      attachmentOnlyPrompt;
    const apiPromptText =
      (
        overrides.apiPrompt ??
        overrides.displayText ??
        this.inputTarget.value
      ).trim() || attachmentOnlyPrompt;
    if (!displayText) return;
    if (this.submitting) return;
    if (this.planBlocksSending()) return;

    this.setLoading(true);
    this.clearError();
    await this.waitForInFlightUploads();

    const pendingAssetUrls = (this.pendingAssetUrls || []).slice();
    this.suppressPlaybookRecommendationOptions = pendingAssetUrls.length > 0;
    const promptWithAsset = this.appendAttachmentsToPrompt(
      apiPromptText,
      pendingAssetUrls
    );

    this.appendUserMessage(displayText, pendingAssetUrls);
    this.messagesHistory.push({ role: "user", content: promptWithAsset });
    this.inputTarget.value = "";
    this.adjustInputHeight();
    this.persistHtmlImportAssetFromUrls(pendingAssetUrls);
    this.persistMarkdownImportAssetFromUrls(pendingAssetUrls);
    this.clearAssetPreview();

    // Open full-screen modal immediately when chat starts (first message sent)
    if (
      this.expandToModalValue &&
      this.messagesHistory.length >= 1 &&
      !this.modalValue
    ) {
      this.openModal();
    }

    this.showLoadingIndicator();

    try {
      this.teardownCable();

      // Generate cable ID and subscribe BEFORE the POST request (same pattern as AsyncModalRequestConfirmation)
      const cableSyncId = Math.random().toString(36).substring(2, 15);
      this.cableConsumer = createConsumer();
      this.cableSubscription = this.cableConsumer.subscriptions.create(
        { channel: "AsyncModalRequestConfirmationChannel", id: cableSyncId },
        {
          connected: () => {
            if (this.chatLiveness) this.chatLiveness.schedule();
          },
          received: (data) => {
            if (data.status === "progress") {
              this.updateLoadingIndicator(data.label);
              if (this.rearmAsyncTimeout) this.rearmAsyncTimeout();
              return;
            }

            if (this.asyncTimeoutId) {
              clearTimeout(this.asyncTimeoutId);
              this.asyncTimeoutId = null;
            }

            this.teardownCable();

            if (data.status === "ready") {
              dispatchBillableActionsUsed(billableActionsFromResponse(data));
              this.updateLoadingIndicator("Done", 100);
              if (data.import_summary && data.builder_url) {
                const summaryText = this.formatImportSummary(
                  data.import_summary
                );
                const message = summaryText
                  ? `Imported your HTML into the builder. ${summaryText}`
                  : "Imported your HTML into the builder.";
                this.appendAssistantMessage(
                  message,
                  [],
                  false,
                  null,
                  true,
                  true,
                  null
                );
                this.messagesHistory.push({
                  role: "assistant",
                  content: message,
                });
                window.location.href = data.builder_url;
              } else {
                const assistantContent = this.buildAssistantContent(data);
                this.appendAssistantMessage(
                  assistantContent.message,
                  assistantContent.recommendations,
                  assistantContent.showChatFallback,
                  assistantContent.clarifyingQuestion,
                  true,
                  true,
                  assistantContent.showChatFallback ? displayText : null,
                  assistantContent.experiment,
                  assistantContent.reportExports
                );
                this.messagesHistory.push({
                  role: "assistant",
                  content: assistantContent.message,
                });
              }
            } else {
              this.showError(
                data.error || "Something went wrong. Please try again."
              );
              const fallback =
                "Sorry, I couldn't process that. Please try again.";
              this.appendAssistantMessage(
                fallback,
                [],
                true,
                null,
                true,
                true,
                displayText
              );
              this.messagesHistory.push({
                role: "assistant",
                content: fallback,
              });
            }

            this.hideLoadingIndicator();
            this.setLoading(false);
          },
          rejected: () => {
            // Handle a rejected subscription (e.g., authorization failure or channel rejection)
            if (this.asyncTimeoutId) {
              clearTimeout(this.asyncTimeoutId);
              this.asyncTimeoutId = null;
            }

            if (this.sendButtonTarget.disabled) {
              this.teardownCable();
              this.showError(
                "Unable to establish a connection. Please try again."
              );
              const fallback =
                "I couldn't establish a stable connection. Please try sending your message again.";
              this.appendAssistantMessage(
                fallback,
                [],
                true,
                null,
                true,
                true,
                displayText
              );
              this.messagesHistory.push({
                role: "assistant",
                content: fallback,
              });
              this.hideLoadingIndicator();
              this.setLoading(false);
            }
          },
        }
      );

      this.armLivenessTimeout(() => {
        this.showError(
          "The request is taking longer than expected. Please try again."
        );
        const fallback =
          "I'm having trouble responding right now. Please try sending your message again.";
        this.appendAssistantMessage(
          fallback,
          [],
          true,
          null,
          true,
          true,
          displayText
        );
        this.messagesHistory.push({ role: "assistant", content: fallback });
        this.hideLoadingIndicator();
        this.setLoading(false);
      });

      // Brief delay to ensure WebSocket subscription is established before enqueuing the job
      await new Promise((resolve) => setTimeout(resolve, 500));

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({
          prompt: promptWithAsset,
          messages: this.messagesHistory.slice(0, -1),
          page_url: window.location.href,
          brand_description: this.brandDescriptionValue?.trim() || undefined,
          brand_tone: this.brandToneValue?.trim() || undefined,
          business_type: this.businessTypeValue?.trim() || undefined,
          playbook_id:
            this.hasPlaybookIdValue && this.playbookIdValue > 0
              ? this.playbookIdValue
              : undefined,
          playbook_name: this.playbookNameValue?.trim() || undefined,
          tutorial_title: this.playbookTutorialTitleValue?.trim() || undefined,
          tutorial_content:
            this.playbookTutorialContentValue?.trim() || undefined,
          related_template_ids: this.parseCsvIdList(
            this.relatedTemplateIdsValue
          ),
          related_playbook_ids: this.parseCsvIdList(
            this.relatedPlaybookIdsValue
          ),
          funnel_stats: this.buildFunnelStats(),
          cta_type:
            this.hasCtaTypeValue && this.ctaTypeValue
              ? this.ctaTypeValue
              : undefined,
          cta_id:
            this.funnelBreakdownChatValue &&
            this.hasCtaIdValue &&
            this.ctaIdValue > 0
              ? this.ctaIdValue
              : undefined,
          variant_id:
            this.funnelBreakdownChatValue &&
            this.hasVariantIdValue &&
            this.variantIdValue > 0
              ? this.variantIdValue
              : undefined,
          funnel_timeframe:
            this.funnelBreakdownChatValue &&
            this.hasFunnelTimeframeValue &&
            this.funnelTimeframeValue
              ? this.funnelTimeframeValue
              : undefined,
          funnel_timeframe_start:
            this.funnelBreakdownChatValue &&
            this.hasFunnelTimeframeStartValue &&
            this.funnelTimeframeStartValue
              ? this.funnelTimeframeStartValue
              : undefined,
          funnel_timeframe_end:
            this.funnelBreakdownChatValue &&
            this.hasFunnelTimeframeEndValue &&
            this.funnelTimeframeEndValue
              ? this.funnelTimeframeEndValue
              : undefined,
          funnel_breakdown_chat: this.funnelBreakdownChatValue || undefined,
          selected_cta_id:
            !this.funnelBreakdownChatValue && this.selectedCtaId > 0
              ? this.selectedCtaId
              : undefined,
          campaign_help_scope:
            !this.funnelBreakdownChatValue && this.campaignHelpScope
              ? this.campaignHelpScope
              : undefined,
          cable_sync_id: cableSyncId,
        }),
      });

      const json = await response.json().catch(() => ({}));

      // Gated response: either actions exhausted or paid custom-templates gate.
      if (response.ok && json.status === "gated") {
        this.teardownCable();
        this.hideLoadingIndicator();
        this.setLoading(false);
        const actionsExhausted = json.feature === "actions_exhausted";
        const gatedMessage =
          json.message ||
          (actionsExhausted
            ? "You've used all included AI actions. Upgrade to continue with ConvertFlow AI."
            : "Custom templates from uploads are available on Pages. We've saved your request.");
        this.appendAssistantMessage(
          gatedMessage,
          [],
          false,
          null,
          true,
          true,
          null
        );
        this.messagesHistory.push({
          role: "assistant",
          content: gatedMessage,
        });
        if (actionsExhausted) {
          this.showRemixAiActionsExhaustedModal();
        } else {
          this.showRemixAiPagesUpgradeModal();
        }
        return;
      }

      if (!response.ok) {
        this.teardownCable();
        this.showError(json.error || "Something went wrong. Please try again.");
        const fallback = "Sorry, I couldn't process that. Please try again.";
        this.appendAssistantMessage(
          fallback,
          [],
          true,
          null,
          true,
          true,
          displayText
        );
        this.messagesHistory.push({ role: "assistant", content: fallback });
        this.hideLoadingIndicator();
        this.setLoading(false);
      }
    } catch (err) {
      this.teardownCable();
      this.showError("Unable to connect. Please try again.");
      const fallback =
        "I'm having trouble connecting. Please check your connection and try again.";
      this.appendAssistantMessage(
        fallback,
        [],
        true,
        null,
        true,
        true,
        displayText
      );
      this.messagesHistory.push({ role: "assistant", content: fallback });
      this.hideLoadingIndicator();
      this.setLoading(false);
    }
  }

  formatImportSummary(importSummary) {
    if (!importSummary || typeof importSummary !== "object") return "";
    const created = importSummary.created || {};
    const parts = [];
    if (created.sections) parts.push(`${created.sections} sections`);
    if (created.section_columns)
      parts.push(`${created.section_columns} columns`);
    if (created.elements) parts.push(`${created.elements} elements`);
    const normalized = Array.isArray(importSummary.normalized)
      ? importSummary.normalized.length
      : 0;
    const mapped = importSummary.mapped || {};
    const mappedCount =
      Number(mapped.headlines || 0) + Number(mapped.strings || 0);
    const repaired = importSummary.repaired || {};
    const repairedCount =
      Number(repaired.headlines || 0) + Number(repaired.strings || 0);
    const skipped = importSummary.skipped || {};
    const skippedCount =
      Number(skipped.headlines || 0) + Number(skipped.strings || 0);
    const visual = importSummary.visual || {};
    const layoutRepaired = Number(repaired.section_order || 0);
    if (
      parts.length === 0 &&
      normalized === 0 &&
      mappedCount === 0 &&
      repairedCount === 0 &&
      layoutRepaired === 0 &&
      skippedCount === 0 &&
      !visual.reference
    )
      return "";
    let text = parts.length ? `Created ${parts.join(", ")}.` : "";
    if (normalized > 0) {
      text += `${text ? " " : ""}Normalized ${normalized} setting${
        normalized === 1 ? "" : "s"
      }.`;
    }
    if (mappedCount > 0) {
      text += `${text ? " " : ""}Mapped ${mappedCount} ${
        mappedCount === 1 ? "item" : "items"
      }.`;
    }
    if (repairedCount > 0) {
      text += `${text ? " " : ""}Repaired ${repairedCount} missing ${
        repairedCount === 1 ? "item" : "items"
      }.`;
    }
    if (layoutRepaired > 0) {
      text += `${
        text ? " " : ""
      }Reordered sections to match the visual reference.`;
    }
    if (visual.reference) {
      const count = Number(visual.reference_count || 1);
      text += `${text ? " " : ""}Used ${count} visual reference${
        count === 1 ? "" : "s"
      } for layout.`;
    }
    if (skippedCount > 0) {
      text += `${text ? " " : ""}Skipped ${skippedCount} ${
        skippedCount === 1 ? "item" : "items"
      }.`;
    }
    return text.trim();
  }

  isGenericCampaignName(name) {
    const normalized = String(name || "")
      .trim()
      .toLowerCase();
    if (!normalized) return true;
    return [
      "generate a custom template",
      "generate custom template",
      "generate template",
      "custom template",
      "imported campaign",
      "imported variant",
    ].includes(normalized);
  }

  humanizeAssetFilename(filename) {
    const base = String(filename || "")
      .trim()
      .replace(/\.[^.]+$/, "");
    if (!base) return "";
    return base
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  resolveCampaignName({ campaignName, displayText, htmlUrl, filename }) {
    if (!this.isGenericCampaignName(campaignName)) {
      return String(campaignName).trim().slice(0, 120);
    }

    const fromFilename = this.humanizeAssetFilename(
      filename || (htmlUrl ? this.getAssetFilename(htmlUrl) : "")
    );
    if (fromFilename) return fromFilename.slice(0, 120);

    if (!this.isGenericCampaignName(displayText)) {
      return String(displayText).trim().slice(0, 120);
    }

    return "Imported Campaign";
  }

  resolveVariantName({ campaignName, displayText, htmlUrl, filename }) {
    const resolved = this.resolveCampaignName({
      campaignName,
      displayText,
      htmlUrl,
      filename,
    });
    return resolved === "Imported Campaign" ? "Imported Variant" : resolved;
  }

  variantFlowCtaId() {
    if (this.hasCtaIdValue && this.ctaIdValue > 0) {
      return this.ctaIdValue;
    }

    const form = document.getElementById("new_variant");
    const input = form?.querySelector('input[name="variant[cta_id]"]');
    const parsed = parseInt(input?.value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  buildJsonImportSettings({
    campaignName,
    displayText,
    ctaType,
    htmlUrl,
    assetFilename,
  }) {
    const settings = {
      campaign_name: this.resolveCampaignName({
        campaignName,
        displayText,
        htmlUrl,
        filename: assetFilename,
      }),
      cta_type: ctaType || "page",
    };

    const existingCtaId = this.isVariantFlow ? this.variantFlowCtaId() : null;
    if (existingCtaId) {
      settings.cta_id = existingCtaId;
      settings.variant_name = this.resolveVariantName({
        campaignName,
        displayText,
        htmlUrl,
        filename: assetFilename,
      });
      if (this.hasCtaTypeValue && this.ctaTypeValue) {
        settings.cta_type = this.ctaTypeValue;
      }
    }

    return settings;
  }

  async submitNativeJsonImport({
    displayText,
    ctaType,
    campaignName,
    imageUrl,
    htmlContent,
    htmlUrl,
    assetFilename,
    buildPrompt,
    scriptSource,
    referenceImageUrls,
    triggerButton,
  }) {
    const restoreTriggerButton = () => {
      this.restoreCustomTemplateTriggerButton(triggerButton);
    };

    const assetParams = {};
    if (htmlContent) assetParams.html_content = htmlContent;
    if (htmlUrl) assetParams.html_url = htmlUrl;
    if (assetFilename) assetParams.filename = assetFilename;
    const companionRefs = [
      ...(Array.isArray(referenceImageUrls) ? referenceImageUrls : []),
      ...(htmlContent || htmlUrl ? [imageUrl] : []),
    ].filter(Boolean);
    if (htmlContent || htmlUrl) {
      if (companionRefs.length) {
        assetParams.reference_image_urls = [...new Set(companionRefs)];
      }
    } else if (imageUrl) {
      assetParams.image_url = imageUrl;
    }
    if (buildPrompt) assetParams.build_prompt = buildPrompt;
    const resolvedScriptSource =
      scriptSource || this.extractScriptSourceFromHtml(htmlContent);
    if (resolvedScriptSource) assetParams.script_source = resolvedScriptSource;

    if (
      !assetParams.html_content &&
      !assetParams.html_url &&
      !assetParams.image_url &&
      !assetParams.build_prompt
    ) {
      this.showError(
        "An image, HTML source, or layout prompt is required for import."
      );
      this.setLoading(false);
      restoreTriggerButton();
      return;
    }

    if (
      this.expandToModalValue &&
      this.messagesHistory.length >= 1 &&
      !this.modalValue
    ) {
      this.openModal();
    }

    this.showLoadingIndicator();

    try {
      this.teardownCable();
      const cableSyncId = Math.random().toString(36).substring(2, 15);
      this.cableConsumer = createConsumer();
      this.cableSubscription = this.cableConsumer.subscriptions.create(
        { channel: "AsyncModalRequestConfirmationChannel", id: cableSyncId },
        {
          connected: () => {
            if (this.chatLiveness) this.chatLiveness.schedule();
          },
          received: (data) => {
            if (data.status === "progress") {
              this.updateLoadingIndicator(data.label);
              if (this.rearmAsyncTimeout) this.rearmAsyncTimeout();
              return;
            }
            if (this.asyncTimeoutId) {
              clearTimeout(this.asyncTimeoutId);
              this.asyncTimeoutId = null;
            }
            this.teardownCable();
            if (data.status === "ready" && data.builder_url) {
              dispatchBillableActionsUsed(billableActionsFromResponse(data));
              this.updateLoadingIndicator("Done", 100);
              const summaryText = this.formatImportSummary(data.import_summary);
              const message = summaryText
                ? `Imported your design into the builder. ${summaryText}`
                : "Imported your design into the builder.";
              this.appendAssistantMessage(
                message,
                [],
                false,
                null,
                true,
                true,
                null
              );
              this.messagesHistory.push({
                role: "assistant",
                content: message,
              });
              window.location.href = data.builder_url;
            } else {
              this.showError(data.error || "Import failed. Please try again.");
              restoreTriggerButton();
            }
            this.hideLoadingIndicator();
            this.setLoading(false);
          },
          rejected: () => {
            this.teardownCable();
            this.showError(
              "Unable to establish a connection. Please try again."
            );
            restoreTriggerButton();
            this.hideLoadingIndicator();
            this.setLoading(false);
          },
        }
      );

      this.armLivenessTimeout(() => {
        this.showError(
          "The request is taking longer than expected. Please try again."
        );
        restoreTriggerButton();
        this.hideLoadingIndicator();
        this.setLoading(false);
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      const websiteId = this.websiteIdValue;
      const importSettings = this.buildJsonImportSettings({
        campaignName,
        displayText,
        ctaType,
        htmlUrl,
        assetFilename,
      });
      const response = await fetch(`/websites/${websiteId}/remix/cta`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({
          import_mode: "json",
          website_id: websiteId,
          cta_id: importSettings.cta_id,
          cable_sync_id: cableSyncId,
          settings: importSettings,
          asset_params: assetParams,
        }),
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        this.teardownCable();
        this.showError(json.error || "Import failed. Please try again.");
        restoreTriggerButton();
        this.hideLoadingIndicator();
        this.setLoading(false);
      }
    } catch (err) {
      this.teardownCable();
      this.showError("Import failed. Please try again.");
      restoreTriggerButton();
      this.hideLoadingIndicator();
      this.setLoading(false);
    }
  }

  buildFunnelStats() {
    const ds = this.element.dataset;
    const stats = {
      current_viewers: ds.playbookChatCurrentViewers,
      previous_viewers: ds.playbookChatPreviousViewers,
      current_conversions: ds.playbookChatCurrentConversions,
      previous_conversions: ds.playbookChatPreviousConversions,
      current_leads: ds.playbookChatCurrentLeads,
      previous_leads: ds.playbookChatPreviousLeads,
      current_orders: ds.playbookChatCurrentOrders,
      previous_orders: ds.playbookChatPreviousOrders,
      current_revenue: ds.playbookChatCurrentRevenue,
      previous_revenue: ds.playbookChatPreviousRevenue,
      current_bookings: ds.playbookChatCurrentBookings,
      previous_bookings: ds.playbookChatPreviousBookings,
      current_custom_goals: ds.playbookChatCurrentCustomGoals,
      previous_custom_goals: ds.playbookChatPreviousCustomGoals,
      custom_goal_label: ds.playbookChatCustomGoalLabel,
    };
    const hasAny = Object.values(stats).some(
      (v) => v !== undefined && v !== ""
    );
    return hasAny ? stats : undefined;
  }

  buildAssistantContent(data) {
    let message = data.message || "";
    const recommendations = data.recommendations || [];
    const experiment = data.experiment || null;
    const clarifyingQuestion = data.clarifying_question || null;
    const reportExports = Array.isArray(data.report_exports)
      ? data.report_exports
      : [];
    const isFallback = !message && recommendations.length === 0 && !experiment;
    if (isFallback) {
      message =
        "What would you like to achieve? Pick an option below or describe it in your own words.";
    }
    const suggestsHumanSupport = this.messageSuggestsHumanSupport(message);
    return {
      message,
      recommendations,
      experiment,
      clarifyingQuestion,
      reportExports,
      isFallback,
      showChatFallback:
        isFallback || !!data.support_response || suggestsHumanSupport,
    };
  }

  handleApplyExperiment(event) {
    const btn = event.target.closest("[data-apply-experiment]");
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();

    try {
      const data = JSON.parse(decodeURIComponent(btn.dataset.applyExperiment));
      const websiteId = data.website_id || this.websiteIdValue;
      const ctaId = data.cta_id;
      const onTargetCtaPage =
        ctaId &&
        new RegExp(`/websites/\\d+/cta/${ctaId}(?:/|\\?|#|$)`).test(
          window.location.pathname + window.location.search
        );

      // Website-level playbook chat has no VariantListReport listener — open the
      // campaign stats page with Duplicate & Remix prefilled (same as Insights).
      if (websiteId && ctaId && !onTargetCtaPage) {
        const params = new URLSearchParams({
          open_duplicate_remix: "1",
          variant_id: String(data.variant_id),
          remix_instructions: data.remix_instructions || "",
          variant_name: data.variant_name || "",
        });
        window.location.href = `/websites/${websiteId}/cta/${ctaId}?${params.toString()}#stats`;
        return;
      }

      document.dispatchEvent(
        new CustomEvent("openDuplicateAndRemix", {
          detail: {
            variantId: data.variant_id,
            remix_instructions: data.remix_instructions,
            variant_name: data.variant_name,
          },
        })
      );
    } catch (e) {
      console.error(
        "[playbook_chat] Error dispatching openDuplicateAndRemix:",
        e
      );
    }
  }

  buildExperimentActionHtml(experiment) {
    if (!experiment?.variant_id || !experiment?.remix_instructions) return "";

    const data = encodeURIComponent(
      JSON.stringify({
        variant_id: experiment.variant_id,
        cta_id: experiment.cta_id || null,
        website_id: experiment.website_id || this.websiteIdValue || null,
        remix_instructions: experiment.remix_instructions,
        variant_name: experiment.variant_name || "",
      })
    );

    return `
      <div class="mt-3 p-4 rounded-xl" style="border: 1px solid #E5E5E5; background: #FAFAFA;">
        <p class="text-xl font-semibold text-gray-800 mb-2">Suggested split-test</p>
        <p class="text-xl text-gray-600 mb-3">${this.escapeHtml(
          this.normalizeAiText(experiment.remix_instructions)
        )}</p>
        <button type="button"
          data-apply-experiment="${data}"
          class="playbook-chat-option inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xl font-semibold text-white border-none cursor-pointer transition-opacity hover:opacity-90"
          style="background: linear-gradient(135deg, #4f6fff 0%, #003FFF 100%);">
          Apply Experiment &rarr;
        </button>
      </div>
    `;
  }

  reportExportButtonsHtml(reportExports = []) {
    if (!Array.isArray(reportExports) || reportExports.length === 0) {
      return "";
    }

    const buttons = reportExports
      .map((exportItem) => {
        const path = String(exportItem?.path || "").trim();
        const label = String(exportItem?.label || "Export CSV").trim();
        if (!path.startsWith("/")) return "";

        return `<a href="${this.escapeHtml(
          path
        )}" class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl no-underline transition-colors cursor-pointer" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;" data-turbolinks="false">${this.escapeHtml(
          label
        )}</a>`;
      })
      .filter(Boolean)
      .join("");

    if (!buttons) return "";
    return `<div class="flex flex-wrap gap-2 mt-3">${buttons}</div>`;
  }

  isReportExportUrl(url) {
    const trimmed = String(url || "").trim();
    if (!trimmed.startsWith("/")) return false;
    return (
      /\.csv(?:\?|$)/i.test(trimmed) ||
      /(?:\?|&)format=csv(?:&|$)/i.test(trimmed)
    );
  }

  /** Removes markdown export links so only the structured Export button(s) are shown. */
  stripReportExportMarkdownLinks(text) {
    if (!text) return "";
    return text
      .replace(/\[[^\]]*\]\((\/[^)]*?)\)/g, (match, url) =>
        this.isReportExportUrl(url) ? "" : match
      )
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  /** True if on cta/new page (e.g. /websites/123/cta/new). Custom template links navigate in-place there. */
  get isOnCtaNewPage() {
    return /\/websites\/\d+\/cta\/new\b/.test(window.location.pathname);
  }

  /** True if on website show/dashboard page (e.g. /websites/123 or /websites/123/). */
  get isOnWebsitesShow() {
    return /^\/websites\/\d+\/?$/.test(window.location.pathname);
  }

  /** True if on website insights (e.g. /websites/123/insights or sub-pages). */
  get isOnWebsitesInsights() {
    return /^\/websites\/\d+\/insights\b/.test(window.location.pathname);
  }

  /** True if on account insights (e.g. /accounts/123/insights). */
  get isOnAccountInsights() {
    return /^\/accounts\/\d+\/insights\b/.test(window.location.pathname);
  }

  get isAccountAgent() {
    return this.accountAgentValue === true || this.isOnAccountInsights;
  }

  /** True if on a campaign show page with the funnel breakdown report. */
  get isOnCtaShow() {
    return /^\/websites\/\d+\/cta\/\d+\/?$/.test(window.location.pathname);
  }

  /** Inline chat beside the Funnel Breakdown report. */
  get isFunnelBreakdownChat() {
    return this.funnelBreakdownChatValue === true;
  }

  isInsightsSidebarEmbed() {
    return (
      this.isOnWebsitesInsights &&
      this.element.classList.contains("playbook-chat-embed-wrapper--sidebar")
    );
  }

  setupInsightsSidebarHeightSync() {
    this.insightsScrollContainer =
      document.querySelector("#content.website") ||
      document.querySelector(".website");
    this.boundSyncInsightsSidebarHeight =
      this.scheduleInsightsSidebarHeightSync.bind(this);
    window.addEventListener("resize", this.boundSyncInsightsSidebarHeight);
    if (this.insightsScrollContainer) {
      this.insightsScrollContainer.addEventListener(
        "scroll",
        this.boundSyncInsightsSidebarHeight,
        { passive: true }
      );
    }
    this.scheduleInsightsSidebarHeightSync();
  }

  teardownInsightsSidebarHeightSync() {
    if (this.insightsSidebarHeightSyncFrame) {
      cancelAnimationFrame(this.insightsSidebarHeightSyncFrame);
      this.insightsSidebarHeightSyncFrame = null;
    }
    if (this.boundSyncInsightsSidebarHeight) {
      window.removeEventListener("resize", this.boundSyncInsightsSidebarHeight);
      if (this.insightsScrollContainer) {
        this.insightsScrollContainer.removeEventListener(
          "scroll",
          this.boundSyncInsightsSidebarHeight
        );
      }
      this.boundSyncInsightsSidebarHeight = null;
    }
    this.insightsScrollContainer = null;
  }

  scheduleInsightsSidebarHeightSync() {
    if (!this.isInsightsSidebarEmbed()) {
      return;
    }
    if (this.insightsSidebarHeightSyncFrame) {
      cancelAnimationFrame(this.insightsSidebarHeightSyncFrame);
    }
    this.insightsSidebarHeightSyncFrame = requestAnimationFrame(() => {
      this.insightsSidebarHeightSyncFrame = null;
      this.syncInsightsSidebarHeight();
    });
  }

  syncInsightsSidebarHeight() {
    if (!this.isInsightsSidebarEmbed()) {
      return;
    }

    const scrollContainer =
      this.insightsScrollContainer ||
      document.querySelector("#content.website") ||
      document.querySelector(".website");
    const stickyTop = 45;
    const bottomInset = 36;
    const minHeight = 320;
    const containerRect = scrollContainer
      ? scrollContainer.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };
    const elementRect = this.element.getBoundingClientRect();
    const stuckTop = containerRect.top + stickyTop;
    const effectiveTop = Math.max(elementRect.top, stuckTop);
    const availableHeight = containerRect.bottom - effectiveTop - bottomInset;
    const height = Math.max(minHeight, Math.floor(availableHeight));

    this.element.style.setProperty("height", `${height}px`, "important");
    this.element.style.setProperty("max-height", `${height}px`, "important");
    this.element.style.setProperty("min-height", `${minHeight}px`, "important");
  }

  /** Initial suggestion options when chat is empty. */
  get basePrefillOptions() {
    let options;
    if (this.isAccountAgent) {
      options = [
        "Account performance",
        "Underperforming websites",
        "Top campaigns",
        "Export report",
      ];
    } else if (this.hasPlaybookIdValue && this.playbookIdValue > 0) {
      options = [
        "How should I use this?",
        "Recommend a template",
        "Generate a custom template",
      ];
    } else if (this.isFunnelBreakdownChat) {
      options = [
        "Where is dropoff highest?",
        "What should I test next?",
        "Summarize this funnel",
      ];
    } else if (this.isOnWebsitesInsights) {
      options = [
        "Audit my funnel",
        "Top campaigns",
        "Active split-tests",
        "Last month funnel revenue",
      ];
    } else if (this.isOnCtaNewPage || this.isOnWebsitesShow) {
      options = [
        "Audit my funnel",
        "Report on top campaigns",
        "Generate a custom template",
        "Improve my conversion rate",
      ];
    } else {
      options = [
        "Audit my funnel",
        "Improve my conversion rate",
        "Report on top campaigns",
        "Generate a custom template",
      ];
    }

    if (this.remixAiGatedValue) {
      return options.filter(
        (option) =>
          option !== GENERATE_CUSTOM_TEMPLATE_PREFILL_OPTION &&
          option !== "Generate a custom template"
      );
    }

    return options;
  }

  get defaultInputPlaceholder() {
    if (this.isFunnelBreakdownChat) {
      return "Ask about dropoff or selections";
    }

    if (this.isAccountAgent || this.isOnAccountInsights) {
      return "Ask about your websites";
    }

    if (this.isOnWebsitesInsights) {
      return "Ask for insights";
    }

    return "Write your objective";
  }

  parseCsvIdList(raw) {
    return String(raw || "")
      .split(",")
      .map((value) => parseInt(value, 10))
      .filter((value) => Number.isInteger(value) && value > 0);
  }

  /** Detects when the AI recommends talking to someone (ConvertFlow team, support, etc.) */
  messageSuggestsHumanSupport(text) {
    if (!text || typeof text !== "string") return false;
    const lower = text.toLowerCase();
    const patterns = [
      "convertflow team",
      "speak with someone",
      "talk to someone",
      "reach out",
      "contact support",
      "contact us",
      "account manager",
      "personalized support",
      "human support",
      "support directly",
      "reaching out to convertflow",
    ];
    return patterns.some((p) => lower.includes(p));
  }

  appendUserMessage(text, assetUrls = []) {
    if (!this.hasMessagesTarget) return;

    const userGravatarUrl = this.userGravatarUrlValue || "";
    const messageMaxWidthClass = this.embedValue ? "max-w-full" : "max-w-[85%]";
    const safeAssetUrls = Array.isArray(assetUrls)
      ? assetUrls.filter((url) => typeof url === "string" && url.length > 0)
      : [];
    const thumbnailHtml =
      safeAssetUrls.length > 0
        ? `<div class="mb-1 flex flex-wrap gap-1.5">${safeAssetUrls
            .map((assetUrl) => this.userMessageAssetThumbnailHtml(assetUrl))
            .join("")}</div>`
        : "";
    const messageHtml = `${thumbnailHtml}${this.escapeHtml(text)}`;
    const div = document.createElement("div");
    div.className = "flex justify-end gap-2 mb-[32px]";
    div.innerHTML = `
      <div class="inline-flex flex-col items-end min-w-0 ${messageMaxWidthClass}">
        <div
          class="playbook-chat-bubble inline-block max-w-full px-5 py-3 text-xl leading-relaxed text-gray-800 whitespace-pre-wrap break-words overflow-hidden"
          style="background-color: #F9F9F9; border: 1px solid #E5E5E5; border-radius: 12px 5px 12px 12px;"
        >${messageHtml}</div>
      </div>
      <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden bg-gray-200">
        <img src="${this.escapeHtml(
          userGravatarUrl
        )}" alt="" class="w-full h-full object-cover" />
      </div>
    `;
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();
  }

  appendAssistantMessage(
    text,
    recommendations,
    showChatFallback = false,
    clarifyingQuestion = null,
    setPlaceholder = true,
    useTypewriter = true,
    supportContext = null,
    experiment = null,
    reportExports = []
  ) {
    if (!this.hasMessagesTarget) return;

    if (showChatFallback && supportContext) {
      this.supportContextForIntercom =
        this.intercomPrefillFromQuestion(supportContext);
    }

    const chatWidth = this.hasMessagesTarget
      ? this.messagesTarget.clientWidth
      : 0;
    const isNarrowChatWidth = chatWidth > 0 && chatWidth < 430;
    const recommendationCardPadding = isNarrowChatWidth ? "16px" : "30px";
    const thumbTileWidth = isNarrowChatWidth
      ? Math.max(150, Math.min(220, chatWidth - 90))
      : 300;
    const thumbTileHeight = isNarrowChatWidth ? 170 : 200;
    const thumbnailsRowClass = isNarrowChatWidth
      ? "flex flex-wrap gap-3 my-4 pb-1"
      : "flex flex-nowrap gap-4 my-4 overflow-x-auto pb-2";

    const hasScopedPlaybookContext =
      this.hasPlaybookIdValue && this.playbookIdValue > 0;
    const experimentHtml = this.buildExperimentActionHtml(experiment);
    const reportExportsHtml = this.reportExportButtonsHtml(reportExports);
    const recsHtml = recommendations
      .map((rec) => {
        const isCustomTemplate = rec.type === "custom_template";
        const openInNewTab = !isCustomTemplate || !this.isOnCtaNewPage;
        const targetAttr = openInNewTab
          ? ' target="_blank" rel="noopener noreferrer"'
          : "";
        const categoryLabel = rec.category
          ? this.escapeHtml(this.normalizeAiText(rec.category))
          : "";
        const customTemplateHighEffort =
          isCustomTemplate &&
          this.isHighEffortCustomTemplateImport({
            imageUrl: rec.image_url,
            imageUrls: rec.image_urls,
          });
        const linkText = isCustomTemplate
          ? GENERATE_TEMPLATE_LABEL
          : categoryLabel
          ? `View ${categoryLabel} Playbook`
          : "View Playbook";
        const primaryTemplate =
          Array.isArray(rec.thumbnails) &&
          rec.thumbnails.find(
            (thumb) =>
              typeof thumb === "object" &&
              thumb &&
              Number.isInteger(Number(thumb.template_id)) &&
              Number(thumb.template_id) > 0
          );
        const primaryTemplateId = primaryTemplate
          ? Number(primaryTemplate.template_id)
          : null;
        const recommendedTemplateButtonText = "Use template";
        const pendingMarkdown = isCustomTemplate
          ? this.resolvePendingMarkdownAssetForImport()
          : { markdown: "", filename: "" };
        let link = "";
        if (
          isCustomTemplate &&
          (rec.image_url ||
            rec.html_source ||
            rec.build_prompt ||
            rec.remix_prompt ||
            pendingMarkdown.markdown)
        ) {
          const pendingHtml = this.resolvePendingHtmlAssetForImport();
          const referenceImageUrls = this.customTemplateReferenceImageUrls(rec);
          const ntPayload = {
            imageUrl: rec.image_url || referenceImageUrls[0] || "",
            referenceImageUrls,
            htmlContent: pendingHtml.htmlContent || rec.html_source || "",
            scriptSource:
              pendingHtml.scriptSource ||
              this.extractScriptSourceFromHtml(
                pendingHtml.htmlContent || rec.html_source || ""
              ) ||
              "",
            buildPrompt:
              pendingMarkdown.markdown ||
              rec.build_prompt ||
              rec.remix_prompt ||
              "",
            ctaType: rec.cta_type || "page",
            campaignName: rec.campaign_name || "",
            displayText: rec.explanation || "",
            assetFilename:
              pendingHtml.filename || pendingMarkdown.filename || "",
          };
          const triggerId = this.storeCustomTemplateTriggerPayload(ntPayload);
          link = this.customTemplateTriggerButtonHtml({
            triggerId,
            highEffort: customTemplateHighEffort,
            hasHtml: !!(ntPayload.htmlContent || ntPayload.buildPrompt),
          });
        } else if (!hasScopedPlaybookContext && rec.link) {
          link = `<a href="${this.escapeHtml(
            rec.link
          )}" class="inline-block px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white hover:text-white font-semibold text-xl no-underline transition-colors" data-turbolinks="false"${targetAttr}>${linkText} →</a>`;
        } else if (
          !hasScopedPlaybookContext &&
          primaryTemplateId &&
          !rec.link
        ) {
          const remixAttr = rec.remix_prompt
            ? ` data-remix-prompt="${this.escapeHtml(rec.remix_prompt)}"`
            : "";
          link = `<button type="button" class="inline-block px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white hover:text-white font-semibold text-xl cursor-pointer transition-colors border-none" data-template-preview-id="${this.escapeHtml(
            String(primaryTemplateId)
          )}"${remixAttr}>${recommendedTemplateButtonText} →</button>`;
        }
        const explanation = rec.explanation
          ? this.formatParagraphs(rec.explanation)
          : "";
        const websiteLabel =
          this.isAccountAgent && rec.website_name
            ? `<p class="mt-0 mb-2 text-xl text-gray-600">${this.escapeHtml(
                rec.website_name
              )}</p>`
            : "";
        const label = isCustomTemplate
          ? GENERATE_TEMPLATE_LABEL
          : categoryLabel
          ? categoryLabel
          : "Playbook";
        const recRemixPrompt = rec.remix_prompt || "";
        const thumbnailCards = (rec.thumbnails || []).map((thumb) => {
          const thumbUrl = typeof thumb === "string" ? thumb : thumb.url || "";
          const templateId =
            typeof thumb === "object" ? thumb.template_id : null;
          const templateName =
            typeof thumb === "object" ? thumb.template_name || "" : "";
          const templateCtaType =
            typeof thumb === "object" ? thumb.cta_type || "" : "";
          const formatLabel = (() => {
            const key = String(templateCtaType || "").trim();
            const map = {
              page: "Landing Page",
              overlay: "Overlay Popup",
              hook: "Hook Popup",
              bar: "Sticky Bar",
              inline: "Embed",
            };
            return map[key] || "";
          })();
          const thumbLabel = (() => {
            const name = String(templateName || "").trim();
            if (formatLabel && name) return `${formatLabel} - ${name}`;
            if (formatLabel) return formatLabel;
            if (name) return name;
            return label;
          })();
          const img = `<img src="${this.escapeHtml(
            thumbUrl
          )}" alt="" class="w-full h-full object-cover rounded-lg" loading="lazy" />`;
          const remixAttr = recRemixPrompt
            ? ` data-remix-prompt="${this.escapeHtml(recRemixPrompt)}"`
            : "";
          const previewOverlay = `<span class="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style="background: rgba(0,0,0,0.8);"><span class="border-solid border-[1px] border-white text-white text-xl font-semibold px-4 py-2 rounded-full" style="background: transparent;">Use Template →</span></span>`;
          const wrappedImg =
            templateId && !this.isAccountAgent
              ? `<button type="button" class="relative w-full h-full min-w-0 min-h-0 border-none bg-transparent p-0 cursor-pointer" data-template-preview-id="${this.escapeHtml(
                  String(templateId)
                )}"${remixAttr}>${img}${previewOverlay}</button>`
              : rec.link
              ? `<a href="${this.escapeHtml(
                  rec.link
                )}" class="block w-full h-full min-w-0 min-h-0" data-turbolinks="false"${targetAttr}>${img}</a>`
              : img;
          return `
            <div class="flex-shrink-0 flex flex-col items-center">
              <div class="rounded-2xl bg-gray-900 p-2 overflow-hidden flex items-center justify-center" style="width: ${thumbTileWidth}px; max-width: ${thumbTileWidth}px; height: ${thumbTileHeight}px; max-height: ${thumbTileHeight}px;">
                ${wrappedImg}
              </div>
              <span class="mt-2 text-center font-semibold text-gray-900 text-xl" style="max-width: ${thumbTileWidth}px;">${this.escapeHtml(
            this.normalizeAiText(thumbLabel)
          )}</span>
            </div>`;
        });
        const thumbnailsRow =
          thumbnailCards.length > 0 &&
          `<div class="${thumbnailsRowClass}">${thumbnailCards.join("")}</div>`;
        return `<div class="mt-2 leading-relaxed" style="border: 1px solid #E5E5E5; border-radius: 5px 12px 12px 12px; padding: ${recommendationCardPadding};">${websiteLabel}${
          explanation || ""
        }${thumbnailsRow || ""}${link}</div>`;
      })
      .filter(Boolean)
      .join("");

    const chatFallbackHtml =
      showChatFallback && typeof window.Intercom === "function"
        ? `<div class="flex flex-wrap gap-2 mt-3"><button type="button" data-action="click->playbook-chat#openIntercomChat" class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl transition-colors cursor-pointer" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">Chat with us</button></div>`
        : showChatFallback
        ? `<p class="mt-2 mb-0 text-gray-600">Need help? Reach out to the ConvertFlow team for personalized recommendations.</p>`
        : "";

    const shouldExtractInlinePathOptions =
      clarifyingQuestion?.options?.length === 0 &&
      recommendations.length === 0 &&
      !experimentHtml &&
      typeof text === "string" &&
      text.includes("?");
    const inlinePathOptions = shouldExtractInlinePathOptions
      ? this.extractInlinePathOptions(text)
      : [];
    const pillOptions =
      clarifyingQuestion?.options?.length > 0
        ? clarifyingQuestion.options
        : inlinePathOptions;
    const optionCtaIds = clarifyingQuestion?.option_cta_ids || [];
    const clarifyingPillsHtml =
      pillOptions.length > 0
        ? pillOptions
            .map((opt, index) => {
              const normalizedOpt = this.normalizeAiText(opt);
              if (this.isGenerateTemplatePrefillOption(normalizedOpt)) {
                return this.customTemplatePillHtml({
                  option: normalizedOpt,
                  variant: "prefill",
                });
              }
              const safeOption = this.escapeHtml(normalizedOpt);
              const ctaId = Number(optionCtaIds[index]);
              const ctaAttr =
                Number.isFinite(ctaId) && ctaId >= 0
                  ? ` data-cta-id="${ctaId}"`
                  : "";
              return `<button type="button" data-action="click->playbook-chat#submitPill" data-option="${safeOption}"${ctaAttr} class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl transition-colors" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">${safeOption}</button>`;
            })
            .join("")
        : "";
    const clarifyingPillsSection =
      clarifyingPillsHtml &&
      `<div class="flex flex-wrap gap-2 mt-3">${clarifyingPillsHtml}</div>`;
    const campaignSearchSection = this.campaignSearchHtml(clarifyingQuestion);

    const playbookCount = recommendations.filter(
      (r) => r.type !== "custom_template"
    ).length;
    const recommendationOptions =
      playbookCount >= 1 ? this.recommendationOptionsForMessage() : [];
    if (this.suppressPlaybookRecommendationOptions) {
      this.suppressPlaybookRecommendationOptions = false;
    }
    const recommendationPillsHtml =
      recommendationOptions.length > 0
        ? recommendationOptions
            .map((opt) => {
              if (this.isGenerateTemplatePrefillOption(opt)) {
                return this.customTemplatePillHtml({
                  option: opt,
                  variant: "recommendation",
                });
              }
              return `<button type="button" data-action="click->playbook-chat#submitPill" data-option="${this.escapeHtml(
                opt
              )}" class="playbook-chat-option inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-lg text-left text-xl transition-colors cursor-pointer" style="background-color: #E6ECFF; border: 1px solid #D0D8FF; color: #003FFF;">${this.escapeHtml(
                opt
              )}</button>`;
            })
            .join("")
        : "";
    const recommendationPillsSection =
      recommendationPillsHtml &&
      `<div class="flex flex-wrap gap-2 mt-3">${recommendationPillsHtml}</div>`;

    const avatarUrl = this.avatarUrlValue || "";
    const messageText =
      reportExportsHtml && text
        ? this.stripReportExportMarkdownLinks(text)
        : text;
    const formattedTextHtml = messageText
      ? `<div class="mb-3 [&>p:last-child]:mb-0">${this.formatParagraphs(
          messageText
        )}</div>`
      : "";
    const restHtml = [
      clarifyingPillsSection,
      campaignSearchSection,
      experimentHtml,
      reportExportsHtml,
      recsHtml,
      recommendationPillsSection,
      chatFallbackHtml,
    ]
      .filter(Boolean)
      .join("");
    const useTypewriterEffect = useTypewriter && text && text.trim().length > 0;

    // Only offer feedback on real assistant replies — not the empty prefill
    // (no user turn yet) or error fallbacks.
    const showFeedback =
      text &&
      text.trim().length > 0 &&
      !showChatFallback &&
      (this.messagesHistory || []).some((message) => message.role === "user");
    const feedbackHtml = showFeedback ? feedbackRowHtml("playbook-chat") : "";

    const div = document.createElement("div");
    const assistantMaxWidthClass = this.embedValue
      ? "max-w-full"
      : "max-w-[calc(85%-2.5rem)]";
    div.className = "flex justify-start gap-2 mb-[32px]";
    div.dataset.playbookChatAssistantMessage = "";
    div.innerHTML = `
      <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 p-2" style="margin-top: 10px">
        <img src="${this.escapeHtml(
          avatarUrl
        )}" alt="" class="w-full h-full object-contain" />
      </div>
      <div class="flex flex-col min-w-0 ${assistantMaxWidthClass}">
        <div class="px-4 py-2.5 text-gray-800 leading-relaxed" style="border-radius: 5px 12px 12px 12px;">
          ${
            useTypewriterEffect
              ? `<div class="playbook-chat-message-text text-xl text-gray-500 mb-3 [&>p:last-child]:mb-0"></div><div class="playbook-chat-message-rest${
                  restHtml ? " opacity-0 max-h-0 overflow-hidden" : ""
                }">${restHtml}</div>`
              : `${formattedTextHtml}${restHtml}`
          }
        </div>
        ${feedbackHtml}
      </div>
    `;
    this.messagesTarget.appendChild(div);
    if (setPlaceholder && this.hasInputTarget) {
      this.inputTarget.placeholder = "Write your response";
    }

    if (useTypewriterEffect) {
      const textEl = div.querySelector(".playbook-chat-message-text");
      const restEl = div.querySelector(".playbook-chat-message-rest");
      const plainText = this.normalizeAiText(text);
      this.runTypewriter(textEl, messageText, formattedTextHtml, () => {
        textEl.classList.remove("text-gray-500");
        if (restEl) {
          restEl.classList.remove("opacity-0", "max-h-0", "overflow-hidden");
        }
        this.disableAllOptionsExceptLatest();
        this.scrollToShowAssistantMessage(div);
      });
    } else {
      this.disableAllOptionsExceptLatest();
      this.scrollToShowAssistantMessage(div);
    }
  }

  submitFeedback(event) {
    submitChatFeedback(event, {
      source: "playbook",
      messages: this.messagesHistory || [],
    });
  }

  /**
   * Animate message text into the container word-by-word, then replace with formatted HTML.
   * @param {HTMLElement} container - element to fill
   * @param {string} plainText - raw text to type
   * @param {string} formattedHtml - final HTML to set when done
   * @param {() => void} onComplete - called when animation finishes
   */
  runTypewriter(container, plainText, formattedHtml, onComplete) {
    if (!container) {
      onComplete?.();
      return;
    }
    const words = plainText.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      container.innerHTML = formattedHtml;
      onComplete?.();
      return;
    }
    const MAX_TYPEWRITER_DURATION_MS = 2000;
    const BASE_TYPEWRITER_DELAY_MS = 14;
    const delayMs = Math.min(
      BASE_TYPEWRITER_DELAY_MS,
      Math.max(1, Math.floor(MAX_TYPEWRITER_DURATION_MS / words.length))
    );
    let index = 0;
    const tick = () => {
      if (index >= words.length) {
        container.innerHTML = formattedHtml;
        onComplete?.();
        return;
      }
      const slice = words.slice(0, index + 1).join(" ");
      container.textContent = slice;
      index += 1;
      this.scrollToBottom();
      this.typewriterTimeoutId = setTimeout(tick, delayMs);
    };
    if (this.typewriterTimeoutId) {
      clearTimeout(this.typewriterTimeoutId);
      this.typewriterTimeoutId = null;
    }
    tick();
  }

  /** Disable options in all assistant messages except the latest (last in DOM). */
  disableAllOptionsExceptLatest() {
    if (!this.hasMessagesTarget) return;
    const messages = this.messagesTarget.querySelectorAll(
      "[data-playbook-chat-assistant-message]"
    );
    messages.forEach((msg, i) => {
      const isLatest = i === messages.length - 1;
      if (isLatest) {
        msg.removeAttribute("data-playbook-chat-options-disabled");
        this.setCampaignSearchInputsDisabled(msg, false);
      } else {
        msg.setAttribute("data-playbook-chat-options-disabled", "true");
        this.setCampaignSearchInputsDisabled(msg, true);
      }
    });
  }

  /** Disable options in a specific assistant message (e.g. immediately on click). */
  disableOptionsInMessage(messageEl) {
    if (!messageEl) return;
    messageEl.setAttribute("data-playbook-chat-options-disabled", "true");
  }

  setCampaignSearchInputsDisabled(messageEl, disabled) {
    if (typeof messageEl?.querySelectorAll !== "function") return;
    messageEl
      .querySelectorAll("[data-playbook-campaign-search-input]")
      .forEach((input) => {
        input.disabled = disabled;
      });
  }

  /** Handle option click: disable immediately to prevent double-clicks, then allow default. */
  handleOptionClick(event) {
    const option = event.target.closest(".playbook-chat-option");
    if (!option) return;
    if (option.closest("[data-playbook-campaign-search-option]")) return;
    const message = option.closest("[data-playbook-chat-assistant-message]");
    if (!message) return;
    const submitsPrompt =
      typeof option.matches === "function" &&
      option.matches('[data-action*="submitPill"]');
    if (
      message.hasAttribute("data-playbook-chat-options-disabled") ||
      (submitsPrompt && this.uploadsBlockSending())
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.disableOptionsInMessage(message);
  }

  /** Scroll within the messages container so the top of the AI response is visible with padding */
  scrollToShowAssistantMessage(element) {
    if (!this.hasMessagesTarget) return;
    const container = this.messagesTarget;
    requestAnimationFrame(() => {
      const padding = 16;
      const elementTopInContent =
        container.scrollTop +
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTop = Math.max(0, elementTopInContent - padding);
    });
  }

  restoreCustomTemplateTriggerButton(btn) {
    if (!btn) return;
    const originalHtml = btn.dataset.originalHtml;
    if (!originalHtml) return;
    btn.disabled = false;
    btn.style.opacity = "";
    btn.style.pointerEvents = "";
    btn.innerHTML = originalHtml;
    delete btn.dataset.originalHtml;
  }

  setCustomTemplateTriggerLoading(btn) {
    if (!btn || btn.disabled) return false;
    btn.dataset.originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.style.opacity = "0.6";
    btn.style.pointerEvents = "none";
    btn.innerHTML =
      '<span class="inline-flex items-center gap-2"><span class="cf-loading-spinner spinner-blue m-0" style="width: 14px; height: 14px;"></span><span>Remixing Funnel</span></span>';
    return true;
  }

  async handleCustomTemplateTrigger(event) {
    const btn = event.target.closest("[data-custom-template-trigger]");
    if (!btn || btn.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.actionsExhaustedValue) {
      this.showRemixAiActionsExhaustedModal(event);
      return;
    }
    if (this.remixAiGatedValue) {
      this.showRemixAiPagesUpgradeModal(event);
      return;
    }

    try {
      const data = this.resolveCustomTemplateTriggerPayload(btn);
      if (!data) {
        throw new Error("Missing custom template import payload");
      }
      if (!this.setCustomTemplateTriggerLoading(btn)) return;

      this.setLoading(true);
      await this.submitNativeJsonImport({
        displayText: data.displayText || data.buildPrompt || "Custom template",
        ctaType: data.ctaType || "page",
        campaignName: data.campaignName,
        imageUrl: data.imageUrl || null,
        referenceImageUrls: data.referenceImageUrls || this.pendingImageUrls(),
        htmlContent: data.htmlContent || null,
        scriptSource: data.scriptSource || null,
        assetFilename: data.assetFilename || null,
        buildPrompt: data.buildPrompt || null,
        triggerButton: btn,
      });
    } catch (e) {
      console.error("[handleCustomTemplateTrigger] Error:", e);
      this.restoreCustomTemplateTriggerButton(btn);
      this.showError("Import failed. Please try again.");
      this.setLoading(false);
      this.hideLoadingIndicator();
    }
  }

  get isVariantFlow() {
    const container = document.getElementById(
      "createVariantFromTemplateModalContainer"
    );
    const form = document.getElementById("new_variant");
    return !!(container && form);
  }

  handleThumbnailPreviewClick(event) {
    const btn = event.target.closest("[data-template-preview-id]");
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const templateId = btn.dataset.templatePreviewId;
    if (!templateId) return;
    const remixPrompt = btn.dataset.remixPrompt || "";
    const eventName = this.isVariantFlow
      ? "openVariantTemplatePreviewModal"
      : "openTemplatePreviewModal";
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          templateId: parseInt(templateId, 10),
          brandStyle: {},
          remixPrompt,
          detectedAssetTheme: this.detectedAssetTheme || null,
        },
      })
    );
  }

  submitPill(event) {
    const option = event.currentTarget?.dataset?.option;
    if (!option) return;
    if (this.uploadsBlockSending() || this.planBlocksSending()) return;
    const message = event.currentTarget?.closest(
      "[data-playbook-chat-assistant-message]"
    );
    this.disableOptionsInMessage(message);
    this.applyCampaignSelectionFromPill(event.currentTarget);
    const apiPrompt = this.insightsPrefillPromptFor(option);
    if (apiPrompt !== option) {
      this.submit({ displayText: option, apiPrompt });
      return;
    }
    this.inputTarget.value = option;
    this.submit();
  }

  applyCampaignSelectionFromPill(pill) {
    if (!pill || this.funnelBreakdownChatValue) return;
    if (!Object.prototype.hasOwnProperty.call(pill.dataset, "ctaId")) return;

    const ctaId = Number(pill.dataset.ctaId);
    if (!Number.isFinite(ctaId)) return;

    if (ctaId > 0) {
      this.selectedCtaId = ctaId;
      this.campaignHelpScope = "campaign";
      return;
    }

    this.selectedCtaId = null;
    this.campaignHelpScope = "general";
  }

  campaignSearchHtml(clarifyingQuestion) {
    const campaigns = Array.isArray(clarifyingQuestion?.campaigns)
      ? clarifyingQuestion.campaigns
      : [];
    if (!clarifyingQuestion?.searchable || campaigns.length === 0) return "";

    const encoded = encodeURIComponent(JSON.stringify(campaigns));
    return `<div class="playbook-chat-campaign-search mt-3 w-full min-w-0 max-w-full" data-playbook-campaign-search data-campaigns="${encoded}">
      <label class="block min-w-0">
        <span class="sr-only">Search campaigns</span>
        <input type="search" data-playbook-campaign-search-input placeholder="Search all campaigns..." class="w-full min-w-0 px-3 py-2 rounded-lg text-xl border border-solid border-slate-200 bg-white text-gray-900 outline-none focus:border-blue-400" autocomplete="off" />
      </label>
      <div data-playbook-campaign-search-results class="hidden mt-2 max-h-48 overflow-y-auto rounded-lg border border-solid border-slate-200 bg-white"></div>
    </div>`;
  }

  parseCampaignSearchCatalog(container) {
    if (!container) return [];
    this.campaignSearchCatalogs ||= new WeakMap();
    if (this.campaignSearchCatalogs.has(container)) {
      return this.campaignSearchCatalogs.get(container);
    }

    try {
      const parsed = JSON.parse(
        decodeURIComponent(container.getAttribute("data-campaigns") || "%5B%5D")
      );
      const catalog = Array.isArray(parsed) ? parsed : [];
      this.campaignSearchCatalogs.set(container, catalog);
      return catalog;
    } catch (_e) {
      const empty = [];
      this.campaignSearchCatalogs.set(container, empty);
      return empty;
    }
  }

  handleCampaignSearchInput(event) {
    const input = event.target.closest("[data-playbook-campaign-search-input]");
    if (!input) return;
    if (
      input
        .closest("[data-playbook-chat-assistant-message]")
        ?.hasAttribute("data-playbook-chat-options-disabled")
    ) {
      return;
    }
    const container = input.closest("[data-playbook-campaign-search]");
    const results = container?.querySelector(
      "[data-playbook-campaign-search-results]"
    );
    if (!container || !results) return;

    const query = input.value.trim().toLowerCase();
    if (!query) {
      results.innerHTML = "";
      results.classList.add("hidden");
      return;
    }

    const matches = this.parseCampaignSearchCatalog(container)
      .filter((campaign) => {
        const label = String(campaign.label || "").toLowerCase();
        const name = String(campaign.name || "").toLowerCase();
        return label.includes(query) || name.includes(query);
      })
      .slice(0, 8);

    if (matches.length === 0) {
      results.innerHTML =
        '<p class="px-3 py-2 mb-0 text-xl text-gray-500">No matching campaigns</p>';
      results.classList.remove("hidden");
      return;
    }

    results.innerHTML = matches
      .map((campaign) => {
        const label = this.escapeHtml(
          campaign.label || campaign.name || `Campaign ${campaign.id}`
        );
        const ctaId = Number(campaign.id);
        return `<button type="button" data-playbook-campaign-search-option data-option="${label}" data-cta-id="${ctaId}" class="playbook-chat-option w-full text-left px-3 py-2 text-xl bg-white hover:bg-slate-50 border-0 cursor-pointer">${label}</button>`;
      })
      .join("");
    results.classList.remove("hidden");
  }

  handleCampaignSearchClick(event) {
    const option = event.target.closest(
      "[data-playbook-campaign-search-option]"
    );
    if (!option) return;
    event.preventDefault();
    const message = option.closest("[data-playbook-chat-assistant-message]");
    if (message?.hasAttribute("data-playbook-chat-options-disabled")) return;
    if (this.uploadsBlockSending() || this.planBlocksSending()) return;
    const results = option.closest("[data-playbook-campaign-search-results]");
    if (results) {
      results.innerHTML = "";
      results.classList.add("hidden");
    }
    this.disableOptionsInMessage(message);
    this.setCampaignSearchInputsDisabled(message, true);
    this.applyCampaignSelectionFromPill(option);
    this.inputTarget.value = option.dataset.option || "";
    this.submit();
  }

  openIntercomChat() {
    if (typeof window.Intercom === "function") {
      const prefill =
        this.supportContextForIntercom ||
        buildSupportChatPrefill(
          this.messagesHistory,
          "I need help finding the right playbook"
        );
      window.Intercom("showNewMessage", prefill);
    }
  }

  intercomPrefillFromQuestion(question) {
    if (!question || typeof question !== "string") return null;
    const trimmed = question.trim().replace(/\s+/g, " ");
    if (trimmed.length <= 80) return trimmed;
    return trimmed.slice(0, 77) + "...";
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  /** Show a pulsing (…) loader in the chat while the AI is generating a response. */
  showLoadingIndicator() {
    if (!this.hasMessagesTarget) return;
    const avatarUrl = this.avatarUrlValue || "";
    const div = document.createElement("div");
    div.setAttribute("data-playbook-chat-loading-indicator", "");
    div.className = "flex justify-start gap-2 mb-[32px]";
    div.innerHTML = `
      <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 p-2" style="margin-top: 10px">
        <img src="${this.escapeHtml(
          avatarUrl
        )}" alt="" class="w-full h-full object-contain" />
      </div>
      <div class="flex flex-col min-w-0 max-w-[calc(85%-2.5rem)]">
        <div class="px-4 py-2.5 text-xl text-gray-800 leading-relaxed flex items-center gap-1 min-h-[2.5rem]" style="border-radius: 5px 12px 12px 12px;">
          <span class="playbook-chat-typing-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      </div>
    `;
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();
  }

  updateLoadingIndicator(label) {
    if (!this.hasMessagesTarget || !label) return;
    const el = this.messagesTarget.querySelector(
      "[data-playbook-chat-loading-indicator]"
    );
    const bubble = el && el.querySelector(".px-4");
    if (!bubble) return;
    // Strip any trailing ellipsis/dots from the label so only the animated
    // typing dots convey the waiting state.
    const cleanLabel = label.replace(/[.\u2026]+\s*$/, "").trim();
    // Inherit the bubble's font (text-gray-800, same size as chat copy) so the
    // status line matches assistant messages; dots and label stay equal.
    bubble.innerHTML = `
      <span>${this.escapeHtml(cleanLabel)}</span>
      <span class="playbook-chat-typing-dots"><span>.</span><span>.</span><span>.</span></span>
    `;
    this.scrollToBottom();
  }

  hideLoadingIndicator() {
    if (!this.hasMessagesTarget) return;
    const el = this.messagesTarget.querySelector(
      "[data-playbook-chat-loading-indicator]"
    );
    if (el) el.remove();
  }

  setLoading(loading) {
    this.submitting = !!loading;
    this.refreshSendButtonState();
  }

  refreshSendButtonState() {
    if (!this.hasSendButtonTarget) return;
    const disabled = !!this.submitting || this.hasInFlightUploads();
    this.sendButtonTarget.disabled = disabled;
    this.sendButtonTarget.classList.toggle("opacity-50", disabled);
    this.sendButtonTarget.classList.toggle("cursor-not-allowed", disabled);
  }

  hasInFlightUploads() {
    return (
      (this.uploadInProgressCount | 0) > 0 ||
      (Array.isArray(this.inFlightUploadPromises) &&
        this.inFlightUploadPromises.length > 0)
    );
  }

  trackInFlightUpload(promise) {
    const tracked = Promise.resolve(promise)
      .catch(() => {})
      .finally(() => {
        this.inFlightUploadPromises = (
          this.inFlightUploadPromises || []
        ).filter((item) => item !== tracked);
        this.refreshSendButtonState();
      });
    this.inFlightUploadPromises = this.inFlightUploadPromises || [];
    this.inFlightUploadPromises.push(tracked);
    this.refreshSendButtonState();
    return tracked;
  }

  finishInFlightUpload() {
    this.uploadInProgressCount = Math.max(0, this.uploadInProgressCount - 1);
    this.refreshSendButtonState();
  }

  async waitForInFlightUploads() {
    const pending = (this.inFlightUploadPromises || []).slice();
    if (!pending.length) return;
    await Promise.allSettled(pending);
  }

  customTemplateReferenceImageUrls(rec) {
    const fromRec = Array.isArray(rec && rec.image_urls)
      ? rec.image_urls.filter((url) => typeof url === "string" && url)
      : [];
    if (fromRec.length) return [...new Set(fromRec)];
    return this.pendingImageUrls();
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  addPendingAssetUrl(url, fileMeta = {}) {
    if (!url || typeof url !== "string") return;
    if (!Array.isArray(this.pendingAssetUrls)) {
      this.pendingAssetUrls = [];
    }
    if (
      !this.pendingAssetMetadata ||
      typeof this.pendingAssetMetadata !== "object"
    ) {
      this.pendingAssetMetadata = {};
    }
    this.pendingAssetMetadata[url] = {
      mimetype: typeof fileMeta.mimetype === "string" ? fileMeta.mimetype : "",
      filename: typeof fileMeta.filename === "string" ? fileMeta.filename : "",
      isHtml: Boolean(fileMeta.is_html || fileMeta.isHtml),
      isUrl: Boolean(fileMeta.is_url || fileMeta.isUrl),
      websiteUrl:
        typeof fileMeta.website_url === "string"
          ? fileMeta.website_url
          : typeof fileMeta.websiteUrl === "string"
          ? fileMeta.websiteUrl
          : "",
      htmlContent:
        typeof fileMeta.html_content === "string"
          ? fileMeta.html_content
          : typeof fileMeta.htmlContent === "string"
          ? fileMeta.htmlContent
          : "",
      scriptSource:
        typeof fileMeta.script_source === "string"
          ? fileMeta.script_source
          : typeof fileMeta.scriptSource === "string"
          ? fileMeta.scriptSource
          : "",
      htmlTruncated: Boolean(fileMeta.html_truncated || fileMeta.htmlTruncated),
      isMarkdown: Boolean(fileMeta.is_markdown || fileMeta.isMarkdown),
      markdownContent:
        typeof fileMeta.markdown_content === "string"
          ? fileMeta.markdown_content
          : typeof fileMeta.markdownContent === "string"
          ? fileMeta.markdownContent
          : "",
      markdownTruncated: Boolean(
        fileMeta.markdown_truncated || fileMeta.markdownTruncated
      ),
    };
    if (!this.pendingAssetUrls.includes(url)) {
      if (this.isImageAsset(url) && this.remainingImageUploadSlots() <= 0) {
        delete this.pendingAssetMetadata[url];
        this.showError(`You can attach up to ${MAX_IMAGE_ATTACHMENTS} images.`);
        return;
      }
      this.pendingAssetUrls.push(url);
    }
  }

  pendingImageUrls() {
    return (this.pendingAssetUrls || []).filter((url) =>
      this.isImageAsset(url)
    );
  }

  remainingImageUploadSlots() {
    const { maxImageAttachments } = this.getAttachmentPromptLimits();
    const inProgress = Math.max(0, this.uploadInProgressCount | 0);
    return Math.max(
      0,
      maxImageAttachments - this.pendingImageUrls().length - inProgress
    );
  }

  pendingRichAttachmentUrls() {
    return (this.pendingAssetUrls || []).filter(
      (url) =>
        this.isHtmlAsset(url) ||
        this.isMarkdownAsset(url) ||
        this.isUrlAsset(url)
    );
  }

  remainingRichAttachmentSlots() {
    const { maxRichAttachments } = this.getAttachmentPromptLimits();
    const inProgress = Math.max(0, this.richAttachmentInProgressCount | 0);
    return Math.max(
      0,
      maxRichAttachments - this.pendingRichAttachmentUrls().length - inProgress
    );
  }

  updateAssetPreview() {
    this.refreshSendButtonState();
    if (!this.hasAssetThumbnailTarget || !this.hasAssetPreviewListTarget)
      return;

    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    const list = this.assetPreviewListTarget;
    list.innerHTML = "";

    const inProgress = Math.max(0, this.uploadInProgressCount | 0);
    const showRow = urls.length > 0 || inProgress > 0;

    if (!showRow) {
      this.assetThumbnailTarget.classList.add("hidden");
      this.assetThumbnailTarget.style.display = "none";
      this.refreshPrefillIfEmpty();
      return;
    }

    this.assetThumbnailTarget.classList.remove("hidden");
    this.assetThumbnailTarget.style.display = "block";

    urls.forEach((url, index) => {
      const tile = document.createElement("div");
      const isImage = this.isImageAsset(url);
      const isUrl = this.isUrlAsset(url);
      tile.className =
        "relative inline-block flex-shrink-0 rounded-lg overflow-hidden border border-gray-300 bg-gray-100 shadow-sm";
      tile.style.width = "120px";
      tile.style.height = "90px";
      if (isImage) {
        tile.style.backgroundSize = "cover";
        tile.style.backgroundPosition = "center";
        tile.style.backgroundImage = `url("${this.escapeCssUrl(url)}")`;
      } else if (isUrl) {
        tile.classList.add("bg-gray-50");
        tile.innerHTML = this.urlAttachmentCardInnerHtml(url, {
          offsetForChatLog: false,
        });
      } else {
        tile.classList.add("bg-gray-50");
        tile.innerHTML = this.htmlAttachmentCardInnerHtml(url, {
          offsetForChatLog: false,
        });
      }

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className =
        "absolute top-1 right-1 w-9 h-9 rounded-full bg-black/75 text-white flex items-center justify-center border-0 cursor-pointer hover:bg-black/85 shadow focus:outline-none";
      btn.setAttribute("aria-label", "Remove file");
      btn.setAttribute("title", "Remove file");
      btn.setAttribute("data-playbook-remove-pending-index", String(index));
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 3L3 9M3 3l6 6"/></svg>`;

      tile.appendChild(btn);
      list.appendChild(tile);
    });

    for (let i = 0; i < inProgress; i += 1) {
      const ph = document.createElement("div");
      ph.className =
        "relative inline-flex flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 shadow-sm";
      ph.style.width = "120px";
      ph.style.height = "90px";
      ph.setAttribute("aria-busy", "true");
      ph.innerHTML =
        '<span class="sr-only">Uploading…</span><div class="playbook-asset-spinner"></div>';
      list.appendChild(ph);
    }

    this.refreshPrefillIfEmpty();
  }

  getAttachmentPromptLimits() {
    return {
      maxRichAttachments: MAX_IMPORTED_ATTACHMENTS,
      maxImageAttachments: MAX_IMAGE_ATTACHMENTS,
      maxHtmlCharsPerFile: 100000,
      maxTotalHtmlChars: 200000,
    };
  }

  appendAttachmentsToPrompt(promptText, assetUrls = []) {
    const urls = Array.isArray(assetUrls)
      ? assetUrls.filter((url) => typeof url === "string" && url.length > 0)
      : [];
    if (urls.length === 0) return promptText;

    const {
      maxRichAttachments,
      maxImageAttachments,
      maxHtmlCharsPerFile,
      maxTotalHtmlChars,
    } = this.getAttachmentPromptLimits();
    let richAttachmentCount = 0;
    let imageAttachmentCount = 0;
    let remainingHtmlChars = maxTotalHtmlChars;
    let skippedRichAttachments = 0;
    let skippedImageAttachments = 0;

    const markers = urls
      .map((url, index) => {
        const stepHeader = `[Attachment step ${index + 1} of ${urls.length}]`;

        if (this.isUrlAsset(url)) {
          if (richAttachmentCount >= maxRichAttachments) {
            skippedRichAttachments += 1;
            return null;
          }
          richAttachmentCount += 1;
          return `${stepHeader}\n[Attached website URL: ${this.getWebsiteUrlAttachment(
            url
          )}]`;
        }

        if (this.isMarkdownAsset(url)) {
          if (richAttachmentCount >= maxRichAttachments) {
            skippedRichAttachments += 1;
            return null;
          }
          richAttachmentCount += 1;
          const mdName = this.getAssetFilename(url) || "uploaded.md";
          const mdRef = this.hostedAssetReference(url, mdName);
          const markdown = this.getAssetMarkdownContent(url);
          if (!markdown) {
            return `${stepHeader}\n[Attached Markdown file: ${mdRef}]`;
          }

          const safeMarkdown =
            remainingHtmlChars > 0 ? markdown.slice(0, remainingHtmlChars) : "";
          remainingHtmlChars -= safeMarkdown.length;
          if (!safeMarkdown) {
            return `${stepHeader}\n[Attached Markdown file: ${mdRef}]`;
          }

          return `${stepHeader}\n[Attached Markdown funnel spec: ${mdRef}]\n${safeMarkdown}`;
        }

        if (!this.isHtmlAsset(url)) {
          if (imageAttachmentCount >= maxImageAttachments) {
            skippedImageAttachments += 1;
            return null;
          }
          imageAttachmentCount += 1;
          return `${stepHeader}\n[Attached image: ${url}]`;
        }

        if (richAttachmentCount >= maxRichAttachments) {
          skippedRichAttachments += 1;
          return null;
        }

        richAttachmentCount += 1;
        const meta = this.getAssetMeta(url);
        const name = this.getAssetFilename(url) || "uploaded.html";
        const htmlRef = this.hostedAssetReference(url, name);
        const htmlContent = this.getAssetHtmlContent(url);
        if (!htmlContent)
          return `${stepHeader}\n[Attached HTML file: ${htmlRef}]`;

        const safeHtmlContent =
          remainingHtmlChars > 0
            ? htmlContent.slice(0, remainingHtmlChars)
            : "";
        const exceededSharedBudget =
          safeHtmlContent.length < htmlContent.length;
        remainingHtmlChars -= safeHtmlContent.length;

        const notes = [];
        if (meta.htmlTruncated) {
          notes.push(`[HTML truncated to ${maxHtmlCharsPerFile} characters]`);
        }
        if (exceededSharedBudget) {
          notes.push(
            `[HTML truncated further to stay within the total ${maxTotalHtmlChars} character attachment budget]`
          );
        }

        const noteBlock = notes.length > 0 ? `\n${notes.join("\n")}` : "";
        if (!safeHtmlContent) {
          return `${stepHeader}\n[Attached HTML file: ${htmlRef}]${noteBlock}`;
        }

        return `${stepHeader}\n[Attached HTML file: ${htmlRef}]${noteBlock}\n${safeHtmlContent}`;
      })
      .filter(Boolean)
      .join("\n");

    const skippedNotes = [];
    if (skippedRichAttachments > 0) {
      skippedNotes.push(
        `[${skippedRichAttachments} additional HTML/URL attachment(s) omitted to stay within attachment limits]`
      );
    }
    if (skippedImageAttachments > 0) {
      skippedNotes.push(
        `[${skippedImageAttachments} additional image(s) omitted to stay within the ${maxImageAttachments} image limit]`
      );
    }
    const skippedNote =
      skippedNotes.length > 0 ? `\n${skippedNotes.join("\n")}` : "";
    return `${promptText}\n\n${markers}${skippedNote}`;
  }

  getAssetMeta(url) {
    if (!url || !this.pendingAssetMetadata) return {};
    return this.pendingAssetMetadata[url] || {};
  }

  isHtmlAsset(url, metadata = null) {
    const meta = metadata ? metadata[url] || {} : this.getAssetMeta(url);
    if (meta.isHtml) return true;
    if (meta.mimetype === "text/html") return true;
    const filename = String(meta.filename || "").toLowerCase();
    if (filename.endsWith(".html") || filename.endsWith(".htm")) return true;
    const urlPath = this.assetPathname(url).toLowerCase();
    return urlPath.endsWith(".html") || urlPath.endsWith(".htm");
  }

  isUrlAsset(url) {
    const meta = this.getAssetMeta(url);
    return Boolean(meta.isUrl);
  }

  isMarkdownAsset(url) {
    const meta = this.getAssetMeta(url);
    if (meta.isMarkdown) return true;
    if (meta.mimetype === "text/markdown") return true;
    const filename = String(meta.filename || "").toLowerCase();
    return filename.endsWith(".md") || filename.endsWith(".markdown");
  }

  isImageAsset(url) {
    return (
      !this.isHtmlAsset(url) &&
      !this.isUrlAsset(url) &&
      !this.isMarkdownAsset(url)
    );
  }

  getAssetMarkdownContent(url) {
    const meta = this.getAssetMeta(url);
    return typeof meta.markdownContent === "string" ? meta.markdownContent : "";
  }

  getAssetFilename(url) {
    const meta = this.getAssetMeta(url);
    const fromMeta = String(meta.filename || "").trim();
    if (fromMeta) return fromMeta;
    const path = this.assetPathname(url);
    const basename = path.split("/").filter(Boolean).pop() || "";
    if (!basename) return "";
    try {
      return decodeURIComponent(basename);
    } catch (_e) {
      return basename;
    }
  }

  // Prefer a durable https upload URL in attachment markers so CRM dumps can
  // reference the hosted file. Fall back to the local filename for local-* refs.
  hostedAssetReference(url, fallbackName) {
    const candidate = String(url || "").trim();
    if (/^https:\/\//i.test(candidate)) return candidate;
    return fallbackName || candidate;
  }

  getAssetHtmlContent(url) {
    const meta = this.getAssetMeta(url);
    return typeof meta.htmlContent === "string" ? meta.htmlContent : "";
  }

  getAssetScriptSource(url) {
    const meta = this.getAssetMeta(url);
    if (typeof meta.scriptSource === "string" && meta.scriptSource.trim()) {
      return meta.scriptSource;
    }
    return (
      this.extractScriptSourceFromHtml(this.getAssetHtmlContent(url)) || ""
    );
  }

  resolvePendingHtmlAssetForImport() {
    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    for (const url of urls) {
      if (!this.isHtmlAsset(url)) continue;

      const htmlContent = this.getAssetHtmlContent(url);
      const scriptSource = this.getAssetScriptSource(url);
      const filename = this.getAssetFilename(url);
      if (htmlContent || scriptSource) {
        return { htmlContent, scriptSource, filename };
      }
    }

    const stored = this.storedHtmlImportAsset || {};
    return {
      htmlContent:
        typeof stored.htmlContent === "string" ? stored.htmlContent : "",
      scriptSource:
        typeof stored.scriptSource === "string" ? stored.scriptSource : "",
      filename: typeof stored.filename === "string" ? stored.filename : "",
    };
  }

  persistHtmlImportAssetFromUrls(urls = []) {
    for (const url of urls) {
      if (!this.isHtmlAsset(url)) continue;

      const htmlContent = this.getAssetHtmlContent(url);
      const scriptSource = this.getAssetScriptSource(url);
      if (!htmlContent && !scriptSource) continue;

      this.storedHtmlImportAsset = {
        htmlContent,
        scriptSource,
        filename: this.getAssetFilename(url),
      };
      return;
    }
  }

  resolvePendingMarkdownAssetForImport() {
    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    for (const url of urls) {
      if (!this.isMarkdownAsset(url)) continue;

      const markdown = this.getAssetMarkdownContent(url);
      if (markdown) {
        return { markdown, filename: this.getAssetFilename(url) };
      }
    }

    const stored = this.storedMarkdownImportAsset || {};
    return {
      markdown: typeof stored.markdown === "string" ? stored.markdown : "",
      filename: typeof stored.filename === "string" ? stored.filename : "",
    };
  }

  persistMarkdownImportAssetFromUrls(urls = []) {
    for (const url of urls) {
      if (!this.isMarkdownAsset(url)) continue;

      const markdown = this.getAssetMarkdownContent(url);
      if (!markdown) continue;

      this.storedMarkdownImportAsset = {
        markdown,
        filename: this.getAssetFilename(url),
      };
      return;
    }
  }

  getWebsiteUrlAttachment(url) {
    const meta = this.getAssetMeta(url);
    const parsed = String(meta.websiteUrl || "").trim();
    return parsed || String(meta.filename || "").trim() || "";
  }

  assetPathname(url) {
    if (!url || typeof url !== "string") return "";
    try {
      return new URL(url).pathname || "";
    } catch (_e) {
      return "";
    }
  }

  htmlAttachmentCardInnerHtml(url, options = {}) {
    const { offsetForChatLog = false } = options;
    const name = this.escapeHtml(this.getAssetFilename(url) || "uploaded.html");
    const offsetStyle = offsetForChatLog ? ' style="margin-top: -44px;"' : "";
    return `
      <div class="h-full w-full flex flex-col items-center justify-center px-2 py-2 text-center"${offsetStyle}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="mb-1 text-gray-500">
          <path d="M14 2H7C5.89543 2 5 2.89543 5 4V20C5 21.1046 5.89543 22 7 22H17C18.1046 22 19 21.1046 19 20V7L14 2Z" stroke="currentColor" stroke-width="1.5"/>
          <path d="M14 2V7H19" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8.5 12H15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M8.5 15H15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span class="text-[10px] font-semibold text-gray-600 leading-tight break-all">${name}</span>
      </div>
    `;
  }

  urlAttachmentCardInnerHtml(url, options = {}) {
    const { offsetForChatLog = false } = options;
    const websiteUrl = this.escapeHtml(
      this.getWebsiteUrlAttachment(url) || "https://example.com"
    );
    const offsetStyle = offsetForChatLog ? ' style="margin-top: -44px;"' : "";
    return `
      <div class="h-full w-full flex flex-col items-center justify-center px-2 py-2 text-center"${offsetStyle}>
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" class="mb-1 text-gray-500">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>
          <path d="M3 12H21" stroke="currentColor" stroke-width="1.5"/>
          <path d="M12 3C14.8 5.6 14.8 18.4 12 21" stroke="currentColor" stroke-width="1.5"/>
          <path d="M12 3C9.2 5.6 9.2 18.4 12 21" stroke="currentColor" stroke-width="1.5"/>
        </svg>
        <span class="text-[9px] font-semibold text-gray-600 uppercase tracking-wide leading-tight">Website URL</span>
        <span class="text-[10px] text-gray-600 leading-tight break-all mt-0.5">${websiteUrl}</span>
      </div>
    `;
  }

  userMessageAssetThumbnailHtml(assetUrl) {
    if (this.isImageAsset(assetUrl)) {
      return `<div class="rounded-lg overflow-hidden border border-gray-300 bg-gray-100 flex-shrink-0" style="width: 120px; height: 90px; background-image: url(&quot;${this.escapeHtml(
        this.escapeCssUrl(assetUrl)
      )}&quot;); background-size: cover; background-position: center;"></div>`;
    }
    if (this.isUrlAsset(assetUrl)) {
      return `<div class="rounded-lg overflow-hidden border border-gray-300 bg-gray-50 flex-shrink-0" style="width: 120px; height: 90px;">${this.urlAttachmentCardInnerHtml(
        assetUrl,
        { offsetForChatLog: true }
      )}</div>`;
    }
    return `<div class="rounded-lg overflow-hidden border border-gray-300 bg-gray-50 flex-shrink-0" style="width: 120px; height: 90px;">${this.htmlAttachmentCardInnerHtml(
      assetUrl,
      { offsetForChatLog: true }
    )}</div>`;
  }

  findFirstPendingImageUrl() {
    const urls = Array.isArray(this.pendingAssetUrls)
      ? this.pendingAssetUrls
      : [];
    return urls.find((url) => this.isImageAsset(url)) || null;
  }

  escapeHtml(text) {
    return String(text ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /** Converts **text** to <strong>text</strong>. Call after escapeHtml for safety. */
  formatBold(text) {
    if (!text) return "";
    return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  }

  /** Decodes URL-encoded sequences (e.g. %20 -> space) that may appear in AI output. */
  decodeUrlEncoded(text) {
    if (!text || typeof text !== "string") return text;
    try {
      return text.replace(/%([0-9A-Fa-f]{2})/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );
    } catch {
      return text;
    }
  }

  /** Decodes HTML entities (e.g. &amp; -> &) before we escape for final render. */
  decodeHtmlEntities(text) {
    if (!text || typeof text !== "string") return text;
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  /** Normalizes common AI encodings so visible text renders as intended. */
  normalizeAiText(text) {
    if (!text || typeof text !== "string") return text;
    let normalized = text;
    // Some model outputs can be double-encoded (e.g. &amp;amp;); decode in capped passes.
    for (let i = 0; i < 3; i += 1) {
      const next = this.decodeHtmlEntities(this.decodeUrlEncoded(normalized));
      if (next === normalized) break;
      normalized = next;
    }
    return normalized;
  }

  formatParagraphs(text) {
    if (!text || typeof text !== "string") return "";
    const escaped = this.escapeHtml(this.normalizeAiText(text));
    const withBold = this.formatBold(escaped);
    const withLinks = this.formatLinks(withBold);
    const withHeadings = this.formatMarkdownHeadings(withLinks);
    const withTables = formatMarkdownTables(withHeadings);
    const tableSplit = new RegExp(
      `(<div class="${AI_CHAT_MD_TABLE_WRAP_CLASS}[\\s\\S]*?<\\/div>)`
    );
    return withTables
      .split(tableSplit)
      .map((part) => {
        if (part.startsWith(`<div class="${AI_CHAT_MD_TABLE_WRAP_CLASS}`)) {
          return part;
        }
        return this.wrapTextParagraphs(part);
      })
      .join("");
  }

  wrapTextParagraphs(text) {
    const trimmed = String(text || "").replace(/^\n+|\n+$/g, "");
    if (!trimmed.trim()) return "";
    const paragraphs = trimmed
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (paragraphs.length <= 1) {
      const single = paragraphs[0] || trimmed;
      return `<p class="mb-0 leading-relaxed">${single.replace(
        /\n/g,
        "<br>"
      )}</p>`;
    }
    return paragraphs
      .map(
        (p) =>
          `<p class="mb-2 leading-relaxed last:mb-0">${p.replace(
            /\n/g,
            "<br>"
          )}</p>`
      )
      .join("");
  }

  /**
   * Extracts conversational "path" options from bullet lists so they can be rendered as pills.
   * Example:
   * - Do you want to build X?
   * - Or do you want Y?
   */
  extractInlinePathOptions(text) {
    if (!text || typeof text !== "string") return [];
    const normalized = this.normalizeAiText(text);
    const lines = normalized.split("\n").map((line) => line.trim());
    const bulletGroups = [];
    let currentGroup = null;
    lines.forEach((line, index) => {
      const bulletMatch = line.match(/^[-*•–—]\s+(.+)$/);
      if (bulletMatch) {
        if (!currentGroup) {
          currentGroup = { start: index, options: [] };
        }
        currentGroup.options.push(
          this.sanitizeInlinePathOption(bulletMatch[1])
        );
      } else if (currentGroup) {
        bulletGroups.push(currentGroup);
        currentGroup = null;
      }
    });
    if (currentGroup) bulletGroups.push(currentGroup);

    const validGroups = bulletGroups
      .map((group) => ({
        ...group,
        options: [...new Set(group.options)].filter(
          (option) => option.length >= 8 && option.length <= 140
        ),
      }))
      .filter(
        (group) => group.options.length >= 2 && group.options.length <= 5
      );

    if (validGroups.length > 0) {
      const questionContextRegex =
        /(would you|are you looking|do you want|which|what would|prefer|pick|choose)/i;
      const scored = validGroups.map((group) => {
        const contextText = lines
          .slice(Math.max(0, group.start - 2), group.start)
          .join(" ");
        const contextScore = questionContextRegex.test(contextText) ? 2 : 0;
        const questionMarkScore = group.options.some((option) =>
          option.endsWith("?")
        )
          ? 1
          : 0;
        return { group, score: contextScore + questionMarkScore };
      });
      scored.sort((a, b) => a.score - b.score);
      const best = scored[scored.length - 1];
      // Only treat bullets as clickable choices when there's explicit question context.
      if (best && best.score >= 2) {
        return best.group.options;
      }
    }

    // Fallback: parse either/or choices in prose (without bullets).
    const prose = normalized.replace(/\s+/g, " ").trim();
    const proseOptions = [];
    const eitherOrPatterns = [
      /would you like(?: me to help you)? to\s+(.+?)\s*,?\s+or\s+would you(?: rather| prefer| like)?\s+to\s+(.+?)(?:\?|$)/i,
      /do you want to\s+(.+?)\s*,?\s+or\s+do you want to\s+(.+?)(?:\?|$)/i,
      /would you prefer to\s+(.+?)\s*,?\s+or\s+(.+?)(?:\?|$)/i,
    ];
    eitherOrPatterns.some((pattern) => {
      const match = prose.match(pattern);
      if (!match) return false;
      proseOptions.push(
        this.sanitizeInlinePathOption(match[1]),
        this.sanitizeInlinePathOption(match[2])
      );
      return true;
    });
    const cleanedProseOptions = [...new Set(proseOptions)].filter(
      (option) => option.length >= 8 && option.length <= 140
    );
    if (cleanedProseOptions.length >= 2 && cleanedProseOptions.length <= 5) {
      return cleanedProseOptions;
    }

    // Fallback: parse inline multi-choice prose, e.g.
    // "Are you looking to improve X, boost Y, or scale Z?"
    const inlineListOptions = this.extractInlineListProseOptions(normalized);
    if (inlineListOptions.length >= 2 && inlineListOptions.length <= 5) {
      return inlineListOptions;
    }
    return [];
  }

  sanitizeInlinePathOption(optionText) {
    if (!optionText || typeof optionText !== "string") return "";
    const cleaned = optionText
      .replace(/^or\s+/i, "")
      .replace(
        /^(are you looking to|would you like to|do you want to|would you prefer to|can you|could you)\s+/i,
        ""
      )
      .replace(/^to\s+/i, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/__(.*?)__/g, "$1")
      .replace(/^\s*["'`]+|["'`]+\s*$/g, "")
      .replace(/[,\s]+(?:or|and)\s*$/i, "")
      .replace(/[,:;.\s]+$/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!cleaned) return "";
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  extractInlineListProseOptions(text) {
    if (!text || typeof text !== "string") return [];
    const compact = this.normalizeAiText(text).replace(/\s+/g, " ").trim();
    const promptPatterns = [
      /are you looking to\s+([^?!.]+)(?:\?|\.|!|$)/i,
      /would you like to\s+([^?!.]+)(?:\?|\.|!|$)/i,
      /do you want to\s+([^?!.]+)(?:\?|\.|!|$)/i,
    ];
    let choiceSegment = "";
    promptPatterns.some((pattern) => {
      const match = compact.match(pattern);
      if (!match) return false;
      choiceSegment = match[1] || "";
      return true;
    });
    if (!choiceSegment) return [];

    const normalizedSegment = choiceSegment
      .replace(/\s*,\s*or\s+/gi, "|")
      .replace(/\s+or\s+/gi, "|")
      .replace(/\s*,\s*and\s+/gi, "|")
      .replace(/\s+and\s+/gi, "|")
      .replace(/\s*,\s*/g, "|");
    const parts = normalizedSegment
      .split("|")
      .map((part) => this.sanitizeInlinePathOption(part))
      .filter((option) => option.length >= 8 && option.length <= 140);
    const unique = [...new Set(parts)];
    return unique.length >= 2 && unique.length <= 5 ? unique : [];
  }

  /** Converts markdown headings like "# Title" into emphasized title lines. */
  formatMarkdownHeadings(text) {
    if (!text) return "";
    return text
      .split("\n")
      .map((line) => {
        const match = line.match(/^\s*#{1,6}\s+(.+?)\s*$/);
        if (!match) return line;
        const headingText = match[1];
        return `<span class="font-bold inline-block" style="font-size: calc(1em + 3px);">${headingText}</span>`;
      })
      .join("\n");
  }

  /** Converts [text](url) to <a href="url">text</a>. Only allows / or http(s) URLs. Call after escapeHtml. */
  formatLinks(text) {
    if (!text) return "";
    let out = text.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (_, linkText, url) => {
      const trimmed = url.trim();
      if (
        trimmed.startsWith("/") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("http://")
      ) {
        const safeUrl = this.escapeHtml(trimmed);
        const decodedLinkText = this.escapeHtml(this.normalizeAiText(linkText));
        return `<a href="${safeUrl}" class="text-blue-600 hover:text-blue-800 font-semibold underline" target="_blank" rel="noopener noreferrer">${decodedLinkText}</a>`;
      }
      return `[${linkText}](${url})`;
    });
    // Linkify any bare support.convertflow.com URL (so plain URLs still become clickable)
    out = out.replace(
      /(^|[\s>])(https:\/\/support\.convertflow\.com\/[^\s<>"]+)/g,
      (_, before, url) =>
        `${before}<a href="${this.escapeHtml(
          url
        )}" class="text-blue-600 hover:text-blue-800 font-semibold underline" target="_blank" rel="noopener noreferrer">${this.escapeHtml(
          url
        )}</a>`
    );
    return out;
  }
}
